<template>
    <div id="contentcontainter" class="standard-mode quaro-audit" style="padding-bottom: 85px">
        <!-- SNACKBAR -->
        <v-snackbar top v-model="notification.show" :color="notification.color" v-if="notification">
            <v-icon>{{ notification.icon }}</v-icon
            ><span class="font-weight-bold pl-2"> {{ notification.message }} </span>
        </v-snackbar>

        <!-- LOADER -->
        <div v-if="loading">
            <div style="display: flex">
                <span class="circularprogress mx-auto">
                    <v-progress-circular indeterminate color="#007a68" size="48"></v-progress-circular>
                </span>
            </div>
        </div>

        <template v-else-if="run">
            <!-- STICKY HEADER: title, status, score and the tab row in one bar — the two full-width
                 hero cards pushed the actual content below the fold -->
            <div class="seoaudit-run__topbar">
                <div class="seoaudit-run__topbarrow">
                    <v-btn icon class="mr-1" @click="$router.push('/ai-suite/audit')" aria-label="Back to audits">
                        <v-icon color="#3d6d66">mdi-arrow-left</v-icon>
                    </v-btn>
                    <div class="seoaudit-run__titles">
                        <h2 class="seoaudit-run__title">{{ run.name || "Audit" }}</h2>
                        <span class="seoaudit-run__subtitle">
                            <span class="q-pill" :class="'q-pill--' + statusColor(run.status)">{{ statusLabel(run.status) }}</span>
                            {{ run.url_count || (run.urls ? run.urls.length : 0) }} URLs · checked on {{ formatDate(run.createdAt) }}
                        </span>
                    </div>

                    <div v-if="run.status === 'done' && run.score_avg !== null && run.score_avg !== undefined" class="seoaudit-run__topscore">
                        <span class="seoaudit-run__topscorevalue" :style="{ color: scoreColorHex(Math.round(run.score_avg)) }">{{ Math.round(run.score_avg) }}</span>
                        <span class="seoaudit-run__topscorelabel">
                            <span class="q-eyebrow">Ø Score</span>
                            <span v-if="resultsLoaded" class="seoaudit-run__topscorehint">{{ issueCount ? "+" + achievablePoints + " pts reachable" : "No issues found" }}</span>
                        </span>
                    </div>

                    <v-btn
                        class="ml-auto q-btn q-btn--primary"
                        depressed
                        rounded
                        color="#007a68"
                        dark
                        @click="reRun()"
                        :loading="reRunning"
                        :disabled="reRunning || !$store.getters.canWriteAiSuite"
                        v-if="run.status === 'done'">
                        <v-icon small left>mdi-refresh</v-icon>Re-check
                    </v-btn>
                </div>

                <!-- TABS: the run page carries independent workspaces — overview, fixes, coverage,
                     passage quality and the page/URL data. Stacking them made one endless scroll. -->
                <!-- WAI-ARIA tabs: one tab stop, arrow keys move between tabs, each panel is labelled by its tab -->
                <div v-if="run.status === 'done'" class="seoaudit-run__tabs" role="tablist" aria-label="Audit sections">
                    <button
                        v-for="(tab, index) in visibleTabs"
                        :key="tab.key"
                        type="button"
                        class="seoaudit-run__tab"
                        :class="{ 'seoaudit-run__tab--active': activeTab === tab.key }"
                        role="tab"
                        :id="'audit-tab-' + tab.key"
                        :aria-selected="activeTab === tab.key ? 'true' : 'false'"
                        :aria-controls="'audit-panel-' + tab.key"
                        :tabindex="activeTab === tab.key ? 0 : -1"
                        @click="activeTab = tab.key"
                        @keydown="onTabKeydown($event, index)">
                        {{ tab.label }}
                        <span v-if="tab.count !== null" class="seoaudit-run__tabcount">{{ tab.count }}</span>
                    </button>
                </div>
            </div>

            <!-- PROGRESS (pending / processing) -->
            <div v-if="isActive" class="seoaudit-run__progress">
                <v-progress-linear rounded height="18" :color="'#007a68'" :value="progressPercent">
                    <span class="seoaudit-run__progresstext">{{ progressDone }}/{{ progressTotal }} URLs checked</span>
                </v-progress-linear>
                <p class="q-muted mt-2 mb-0" style="font-size: 13px">The audit is running. This page updates automatically.</p>
            </div>

            <!-- FAILED -->
            <v-alert v-if="run.status === 'failed'" type="error" class="mt-5 mb-0" text>
                {{ run.error || "The audit failed. Please try again later." }}
            </v-alert>

            <!-- one workspace per tab, each at full width -->
            <div class="seoaudit-run__columns">
                <div v-show="activeTab === 'overview'" class="seoaudit-run__panel" role="tabpanel" id="audit-panel-overview" aria-labelledby="audit-tab-overview" tabindex="0">
                    <!-- TWO COLUMNS: diagnosis on the left (scores, facts, coverage, passages),
                         action on the right (progress, top fixes, the material to paste) -->
                    <div class="seoaudit-run__ovgrid">
                        <div class="seoaudit-run__ovcol">
                            <!-- SCORE BY CATEGORY — a tile click filters the fixes and jumps there -->
                            <v-card class="rounded_card quaro-shadow seoaudit-run__sidecard">
                                <v-card-text>
                                    <span class="q-eyebrow">Score by category</span>
                                    <div class="seoaudit-run__catlist">
                                        <div v-for="tile in categoryTiles" :key="tile.key" class="seoaudit-run__cat" role="button" tabindex="0" @click="openCategory(tile)" @keydown.enter.prevent="openCategory(tile)" @keydown.space.prevent="openCategory(tile)">
                                            <div class="seoaudit-run__catrow">
                                                <span class="seoaudit-run__tilelabel">{{ tile.label }}</span>
                                                <span v-if="tile.failCount + tile.warnCount" class="seoaudit-run__tilenote seoaudit-run__tilenote--issue">
                                                    {{ tile.failCount + tile.warnCount }} issue{{ tile.failCount + tile.warnCount > 1 ? "s" : "" }}
                                                </span>
                                                <span v-else class="seoaudit-run__tilenote seoaudit-run__tilenote--ok">All passed</span>
                                                <span class="seoaudit-run__catscore" :style="{ color: scoreColorHex(tile.score) }">{{ tile.score !== null ? tile.score : "-" }}</span>
                                            </div>
                                            <div class="seoaudit-run__tilebar">
                                                <div class="seoaudit-run__tilebarfill" :style="{ width: (tile.score || 0) + '%' }"></div>
                                            </div>
                                        </div>
                                    </div>
                                </v-card-text>
                            </v-card>
                            <!-- PAGE FACTS -->
                            <v-card v-if="detail" class="rounded_card quaro-shadow seoaudit-run__sidecard">
                                <v-card-text>
                                    <span class="q-eyebrow">Page facts</span>
                                    <div class="seoaudit-run__signal">
                                        <span class="seoaudit-run__dot" :class="'q-dot--' + jsChip(detailJsVerdict).color"></span>
                                        <span>{{ jsChip(detailJsVerdict).label }}</span>
                                    </div>
                                    <div v-if="detailFactsPer100 !== null" class="seoaudit-run__signal">
                                        <span class="seoaudit-run__dot q-dot--primary"></span>
                                        <span>{{ detailFactsPer100 }} facts / 100 words</span>
                                    </div>
                                    <div v-if="run.project_id" class="seoaudit-run__signal">
                                        <span class="seoaudit-run__dot" :class="detailCitedCount ? 'q-dot--primary' : 'q-dot--warn'"></span>
                                        <span>{{ detailCitedCount }} citation{{ detailCitedCount === 1 ? "" : "s" }} in AI answers</span>
                                    </div>
                                </v-card-text>
                            </v-card>
                            <!-- COVERAGE AT A GLANCE -->
                            <div v-if="showCoverageCard" class="seoaudit-run__ovcoverage">
                                <div class="seoaudit-run__ovstats">
                                    <div v-if="showFocusStat" class="seoaudit-run__ovstat">
                                        <span class="seoaudit-run__ovstatvalue seoaudit-run__ovstatvalue--accent">{{ coverageStats.focusScore === null ? "-" : coverageStats.focusScore + "%" }}</span>
                                        <span class="seoaudit-run__ovstatlabel">Focus topics</span>
                                    </div>
                                    <div v-if="showPromptStat" class="seoaudit-run__ovstat">
                                        <span class="seoaudit-run__ovstatvalue">{{ promptsNotRelevant || coverageStats.promptScore === null ? "-" : coverageStats.promptScore + "%" }}</span>
                                        <span class="seoaudit-run__ovstatlabel">Tracked prompts</span>
                                    </div>
                                    <div v-if="showFanoutStat" class="seoaudit-run__ovstat">
                                        <span class="seoaudit-run__ovstatvalue seoaudit-run__ovstatvalue--warn">{{ fanoutNotRelevant || coverageStats.fanoutScore === null ? "-" : coverageStats.fanoutScore + "%" }}</span>
                                        <span class="seoaudit-run__ovstatlabel">AI sub-questions</span>
                                    </div>
                                </div>
                                <div class="seoaudit-run__ovcoveragefoot">
                                    <span v-if="coverageGaps.length">{{ coverageGaps.length }} content gap{{ coverageGaps.length > 1 ? "s" : "" }} block the prompts this page should own.</span>
                                    <span v-else>Every analyzed topic is covered on this page.</span>
                                    <span class="seoaudit-run__ovlink seoaudit-run__ovlink--accent ml-auto" role="button" tabindex="0" @click="activeTab = 'coverage'" @keydown.enter.prevent="activeTab = 'coverage'" @keydown.space.prevent="activeTab = 'coverage'">
                                        See coverage →
                                    </span>
                                </div>
                            </div>
                            <!-- PASSAGE QUALITY -->
                            <v-card
                                v-if="analyzedSections.length"
                                class="rounded_card quaro-shadow seoaudit-run__sidecard seoaudit-run__ovcard"
                                role="button"
                                tabindex="0"
                                @click="activeTab = 'passages'"
                                @keydown.enter.prevent="activeTab = 'passages'"
                                @keydown.space.prevent="activeTab = 'passages'">
                                <v-card-text>
                                    <span class="q-eyebrow">Passage quality</span>
                                    <span class="seoaudit-run__ovcardvalue">{{ analyzedSections.length }} sections, {{ problemSections.length }} need work</span>
                                    <span class="seoaudit-run__ovcardhint">Section-by-section review →</span>
                                </v-card-text>
                            </v-card>
                        </div>

                        <div class="seoaudit-run__ovcol">
                            <!-- SINCE THE LAST AUDIT — only when an earlier audit of the same URL exists -->
                            <v-card v-if="progressSummary" class="rounded_card quaro-shadow seoaudit-run__sidecard">
                                <v-card-text>
                                    <div class="seoaudit-run__sidehead">
                                        <span class="q-eyebrow">Since the last audit</span>
                                        <span class="seoaudit-run__progressdate ml-auto">{{ progressSummary.dateLabel }}</span>
                                    </div>
                                    <template v-if="progressSummary.comparable">
                                        <div class="seoaudit-run__progressrow">
                                            <span class="seoaudit-run__progressdelta" :class="'seoaudit-run__progressdelta--' + progressSummary.trend">{{ progressSummary.deltaLabel }}</span>
                                            <span class="seoaudit-run__progresstextline">{{ progressSummary.deltaText }}</span>
                                        </div>
                                        <div v-if="progressSummary.resolved.length" class="seoaudit-run__progresslist">
                                            <span class="seoaudit-run__progresshead">Resolved</span>
                                            <span v-for="entry in progressSummary.resolved" :key="'resolved-' + entry.id" class="q-pill q-pill--outline q-pill--primary">{{ entry.text }}</span>
                                        </div>
                                        <div v-if="progressSummary.regressed.length" class="seoaudit-run__progresslist">
                                            <span class="seoaudit-run__progresshead">New issues</span>
                                            <span v-for="entry in progressSummary.regressed" :key="'regressed-' + entry.id" class="q-pill q-pill--outline seoaudit-run__progresspill--issue">{{
                                                entry.text
                                            }}</span>
                                        </div>
                                        <p v-if="progressSummary.hasLlmJudged" class="seoaudit-run__progressnote">
                                            * judged by the AI analysis — can change slightly between two audits of an unchanged page.
                                        </p>
                                    </template>
                                    <p v-else class="seoaudit-run__progressnote">
                                        The analysis was improved since your last audit, so the two results cannot be compared directly. Changes are shown from the next audit on.
                                    </p>
                                </v-card-text>
                            </v-card>
                            <!-- TOP FIXES -->
                            <v-card class="rounded_card quaro-shadow seoaudit-run__sidecard">
                                <v-card-text>
                                    <div class="seoaudit-run__sidehead">
                                        <span class="q-eyebrow">Do these first</span>
                                        <span v-if="aggregatedActions.length" class="seoaudit-run__ovlink ml-auto" role="button" tabindex="0" @click="activeTab = 'fixes'" @keydown.enter.prevent="activeTab = 'fixes'" @keydown.space.prevent="activeTab = 'fixes'">
                                            All {{ aggregatedActions.length }} fixes →
                                        </span>
                                    </div>
                                    <div v-if="resultsLoading" style="display: flex">
                                        <v-progress-circular indeterminate color="#007a68" class="mx-auto my-4" size="24"></v-progress-circular>
                                    </div>
                                    <div v-else-if="!topActions.length" class="seoaudit-run__emptystate">
                                        <v-icon color="#007a68" size="32" class="mr-3">mdi-check-decagram</v-icon>
                                        <span>No issues found — this page is well set up for LLMs.</span>
                                    </div>
                                    <div v-else class="seoaudit-run__toplist">
                                        <div v-for="action in topActions" :key="'top-' + action.id" class="seoaudit-run__toprow" role="button" tabindex="0" @click="openFix(action)" @keydown.enter.prevent="openFix(action)" @keydown.space.prevent="openFix(action)">
                                            <span class="q-pill q-pill--block" :class="action.worstStatus === 'fail' ? 'q-pill--danger' : 'q-pill--warn'" style="min-width: 56px">
                                                {{ action.worstStatus === "fail" ? "High" : "Medium" }}
                                            </span>
                                            <span class="seoaudit-run__toptitle">{{ action.label }}</span>
                                            <span class="seoaudit-run__actionimpact ml-auto">+{{ action.impact }} pts</span>
                                        </div>
                                    </div>
                                </v-card-text>
                            </v-card>
                            <!-- READY TO PASTE: the generated material itself (one line per block, copy on each),
                                 not a teaser — the full blocks with placeholder highlighting live in the Fixes tab -->
                            <v-card class="rounded_card quaro-shadow seoaudit-run__sidecard seoaudit-run__ovmaterial">
                                <v-card-text>
                                    <div class="seoaudit-run__sidehead">
                                        <span class="q-eyebrow">Ready to paste</span>
                                        <span v-if="fixesGenerated && fixBlockCount" class="q-pill q-pill--outline q-pill--primary ml-2">{{ fixBlockCount }} block{{ fixBlockCount === 1 ? "" : "s" }}</span>
                                        <span
                                            v-if="fixesGenerated && fixBlockCount"
                                            class="seoaudit-run__ovlink ml-auto"
                                            role="button"
                                            tabindex="0"
                                            @click="activeTab = 'fixes'"
                                            @keydown.enter.prevent="activeTab = 'fixes'" @keydown.space.prevent="activeTab = 'fixes'">
                                            Open all →
                                        </span>
                                    </div>
                                    <!-- multi-URL runs: material belongs to one page -->
                                    <template v-if="!detail">
                                        <p class="q-muted seoaudit-run__ovmaterialintro">Material is generated per page. Open a URL in the URLs tab to generate title, intro, FAQ and schema for it.</p>
                                        <span class="seoaudit-run__ovlink" role="button" tabindex="0" @click="activeTab = 'page'" @keydown.enter.prevent="activeTab = 'page'" @keydown.space.prevent="activeTab = 'page'">Go to URLs →</span>
                                    </template>
                                    <template v-else-if="!fixesGenerated">
                                        <p class="q-muted seoaudit-run__ovmaterialintro">
                                            Copy-paste-ready title, meta description, answer-first intro, FAQ and schema, written from this page and its gaps.
                                        </p>
                                        <v-btn class="q-btn q-btn--primary" depressed rounded color="#007a68" dark :disabled="materialButtonDisabled" @click="generateFixSuggestions(false)">
                                            <v-progress-circular v-if="fixesLoading" indeterminate size="16" width="2" color="#ffffff" class="mr-2"></v-progress-circular>
                                            <v-icon v-else small left>mdi-auto-fix</v-icon>
                                            {{ fixesLoading ? "Generating…" : "Generate material" }}
                                        </v-btn>
                                    </template>
                                    <template v-else-if="overviewMaterial.length">
                                        <div v-for="item in overviewMaterial" :key="item.key" class="seoaudit-run__ovmatrow">
                                            <div class="seoaudit-run__ovmatbody">
                                                <span class="seoaudit-run__ovmatlabel">{{ item.label }}</span>
                                                <span class="seoaudit-run__ovmatpreview">{{ item.preview }}</span>
                                            </div>
                                            <v-btn icon small class="seoaudit-run__ovmatcopy" :aria-label="'Copy ' + item.label" @click="copyToClipboard(item.text, item.label + ' copied')">
                                                <v-icon small color="#007a68">mdi-content-copy</v-icon>
                                            </v-btn>
                                        </div>
                                        <span
                                            v-if="fixBlockCount > overviewMaterial.length"
                                            class="seoaudit-run__ovlink d-block mt-3"
                                            role="button"
                                            tabindex="0"
                                            @click="activeTab = 'fixes'"
                                            @keydown.enter.prevent="activeTab = 'fixes'" @keydown.space.prevent="activeTab = 'fixes'">
                                            All {{ fixBlockCount }} blocks in Fixes →
                                        </span>
                                    </template>
                                    <p v-else class="q-muted seoaudit-run__ovmaterialintro mb-0">{{ fixesEmptyText }}</p>
                                </v-card-text>
                            </v-card>
                        </div>
                    </div>
                </div>

                <div v-show="activeTab === 'fixes'" class="seoaudit-run__panel" role="tabpanel" id="audit-panel-fixes" aria-labelledby="audit-tab-fixes" tabindex="0">
                    <v-card v-if="run.status === 'done'" class="rounded_card quaro-shadow seoaudit-run__actionscard">
                        <v-card-text>
                            <div class="seoaudit-run__actionshead">
                                <h3 class="mb-0">Tackle this first</h3>
                                <span v-if="resultsLoaded && filteredActions.length" class="q-pill q-pill--primary q-pill--outline ml-3">{{ filteredActions.length }}</span>
                                <span v-if="activeCategory" class="seoaudit-run__clearfilter ml-auto" role="button" tabindex="0" @click="activeCategory = null" @keydown.enter.prevent="activeCategory = null" @keydown.space.prevent="activeCategory = null">
                                    Clear „{{ categoryLabel(activeCategory) }}" filter ✕
                                </span>
                            </div>

                            <div v-if="resultsLoading" style="display: flex">
                                <v-progress-circular indeterminate color="#007a68" class="mx-auto my-6"></v-progress-circular>
                            </div>

                            <template v-else-if="resultsLoaded">
                                <!-- EMPTY STATE -->
                                <div v-if="!aggregatedActions.length && !llmActionRecommendations.length" class="seoaudit-run__emptystate mt-4">
                                    <v-icon color="#007a68" size="42" class="mr-3">mdi-check-decagram</v-icon>
                                    <span>No issues found — this page is well set up for LLMs.</span>
                                </div>

                                <template v-else>
                                    <div v-for="action in filteredActions" :key="action.id" class="seoaudit-run__action">
                                        <div class="seoaudit-run__actionprio">
                                            <span class="q-pill q-pill--block" :class="action.worstStatus === 'fail' ? 'q-pill--danger' : 'q-pill--warn'">
                                                {{ action.worstStatus === "fail" ? "High" : "Medium" }}
                                            </span>
                                            <span class="seoaudit-run__actionimpact">+{{ action.impact }} pts</span>
                                        </div>
                                        <div class="seoaudit-run__actionbody">
                                            <div class="seoaudit-run__actiontitle">
                                                <span class="font-weight-bold">{{ action.label }}</span>
                                                <span class="q-tag">{{ categoryLabel(action.category) }}</span>
                                            </div>
                                            <div v-if="actionRecommendation(action)" class="seoaudit-run__actionreco">{{ actionRecommendation(action) }}</div>

                                            <!-- 1-URL runs: measured details inline, expandable -->
                                            <template v-if="!isMultiUrl">
                                                <button type="button" class="seoaudit-run__expandtoggle" :aria-expanded="expandedActions[action.id] ? 'true' : 'false'" @click="toggleAction(action.id)">
                                                    {{ expandedActions[action.id] ? "Hide details" : "Measurement & implementation" }}
                                                    <span aria-hidden="true">{{ expandedActions[action.id] ? "▴" : "▾" }}</span>
                                                </button>
                                                <div v-if="expandedActions[action.id]" class="seoaudit-run__actiondetailbox">
                                                    <div v-if="action.affected[0] && action.affected[0].details" class="seoaudit-run__detailrow">
                                                        <span class="seoaudit-run__detaillabel">Measured</span>
                                                        <span class="seoaudit-run__detailvalue">{{ action.affected[0].details }}</span>
                                                    </div>
                                                    <div v-if="action.affected[0] && action.affected[0].recommendation" class="seoaudit-run__detailrow">
                                                        <span class="seoaudit-run__detaillabel">How to fix</span>
                                                        <span class="seoaudit-run__detailtext">{{ action.affected[0].recommendation }}</span>
                                                    </div>
                                                </div>
                                            </template>

                                            <!-- multi-URL runs: per-URL details behind a per-row expander -->
                                            <template v-else>
                                                <button type="button" class="seoaudit-run__expandtoggle" :aria-expanded="expandedActions[action.id] ? 'true' : 'false'" @click="toggleAction(action.id)">
                                                    {{ action.affected.length }} of {{ totalUrlCount }} URLs affected
                                                    <v-icon small color="#007a68">{{ expandedActions[action.id] ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
                                                </button>
                                                <div v-if="expandedActions[action.id]" class="seoaudit-run__actionurls">
                                                    <div v-for="entry in action.affected" :key="action.id + '-' + entry.result_id" class="seoaudit-run__actionurl">
                                                        <span class="seoaudit-run__url">{{ entry.url }}</span>
                                                        <span v-if="entry.details" class="seoaudit-run__actionurldetails">{{ entry.details }}</span>
                                                    </div>
                                                </div>
                                            </template>
                                        </div>
                                    </div>

                                    <!-- AI CONTENT RECOMMENDATIONS: the hub for the generated material.
                                         One generation call fills every row, so the trigger sits in the header. -->
                                    <template v-if="llmActionRecommendations.length">
                                        <div class="seoaudit-run__recohead">
                                            <h4 class="mb-0">AI content recommendations</h4>
                                            <!-- the list is aggregated over every URL, the generated material belongs to the
                                                 opened one — in stacked multi-URL layouts that URL is named right here -->
                                            <span v-if="materialScopeUrl" class="seoaudit-run__recoscope">Material for {{ materialScopeUrl }}</span>
                                            <v-btn class="ml-auto" small rounded outlined color="#007a68" @click="copyAllRecommendations()">
                                                <v-icon small left>mdi-content-copy</v-icon>Copy all recommendations
                                            </v-btn>
                                            <!-- the fix brief lives on the card below — but the card is hidden once every block
                                                 sits on its recommendation, so it moves up here instead of disappearing -->
                                            <v-btn v-if="showHeaderFixBrief" small rounded outlined color="#007a68" @click="copyAllFixes()">
                                                <v-icon small left>mdi-content-copy</v-icon>Copy fix brief
                                            </v-btn>
                                            <!-- same generation flow as the card below, same loading state: one of the two
                                                 can never spin while the other looks idle -->
                                            <v-btn
                                                v-if="!fixesGenerated"
                                                class="q-btn q-btn--primary"
                                                small
                                                rounded
                                                depressed
                                                color="#007a68"
                                                dark
                                                :disabled="materialButtonDisabled"
                                                @click="generateFixSuggestions(false)">
                                                <v-progress-circular v-if="fixesLoading" indeterminate size="14" width="2" color="#ffffff" class="mr-2"></v-progress-circular>
                                                <v-icon v-else small left>mdi-auto-fix</v-icon>
                                                {{ fixesLoading ? "Generating…" : "Generate material" }}
                                            </v-btn>
                                            <v-btn v-else small text class="q-btn--ghost" :disabled="materialButtonDisabled" @click="generateFixSuggestions(true)">
                                                <v-progress-circular v-if="fixesLoading" indeterminate size="14" width="2" color="#3d6d66" class="mr-2"></v-progress-circular>
                                                <v-icon v-else small left>mdi-refresh</v-icon>
                                                {{ fixesLoading ? "Generating…" : "Regenerate" }}
                                            </v-btn>
                                        </div>
                                        <p v-if="materialHeaderCaption" class="q-muted seoaudit-run__recocaption">{{ materialHeaderCaption }}</p>
                                        <!-- travels with the fix brief: whichever of the two places renders the brief also
                                             carries the disclaimer, so it is never lost with the card -->
                                        <span v-if="showHeaderFixBrief && fixesGeneratedAt" class="seoaudit-run__fixescaption seoaudit-run__fixescaption--head">
                                            Generated {{ formatDate(fixesGeneratedAt) }} · always review before publishing.
                                        </span>
                                        <div v-for="row in recommendationRows" :key="row.key" class="seoaudit-run__reco">
                                            <span class="q-pill q-pill--block" :class="'q-pill--' + priorityColor(row.priority)" style="min-width: 62px">{{ priorityLabel(row.priority) }}</span>
                                            <div class="seoaudit-run__recobody">
                                                <span>{{ row.text }}</span>
                                                <span v-if="isMultiUrl" class="seoaudit-run__recourl">{{ row.url }}</span>

                                                <!-- material generated for THIS recommendation, collapsed by default -->
                                                <template v-if="row.blocks.length">
                                                    <!-- once every block is bound this toggle is the ONLY route to the generated
                                                         material, so it has to work without a mouse -->
                                                    <span
                                                        class="seoaudit-run__expandtoggle seoaudit-run__recotoggle"
                                                        role="button"
                                                        tabindex="0"
                                                        :aria-expanded="isRecoMaterialOpen(row) ? 'true' : 'false'"
                                                        @click="toggleRecoMaterial(row)"
                                                        @keydown.enter.prevent="toggleRecoMaterial(row)"
                                                        @keydown.space.prevent="toggleRecoMaterial(row)">
                                                        <span class="seoaudit-run__recocaret">{{ isRecoMaterialOpen(row) ? "▾" : "▸" }}</span>
                                                        <span>Ready-to-paste material</span>
                                                        <span v-for="block in row.blocks" :key="row.key + '-kind-' + block.key" class="seoaudit-run__chip seoaudit-run__blockkind">{{
                                                            block.kindLabel
                                                        }}</span>
                                                    </span>
                                                    <div v-if="isRecoMaterialOpen(row)" class="seoaudit-run__recomaterial">
                                                        <div v-for="block in row.blocks" :key="row.key + '-' + block.key" class="seoaudit-run__fixblock">
                                                            <div class="seoaudit-run__fixhead">
                                                                <span class="font-weight-bold seoaudit-run__blocktitle">{{ block.title }}</span>
                                                                <v-btn
                                                                    icon
                                                                    x-small
                                                                    class="ml-auto"
                                                                    @click="copyToClipboard(block.content, block.title + ' copied')"
                                                                    :aria-label="'Copy ' + block.title">
                                                                    <v-icon small color="#007a68">mdi-content-copy</v-icon>
                                                                </v-btn>
                                                            </div>
                                                            <span v-if="block.note" class="seoaudit-run__blocknote">{{ block.note }}</span>
                                                            <!-- same rules as in the card below: interpolation only, never v-html -->
                                                            <pre v-if="block.preformatted" class="seoaudit-run__pre" :class="block.wraps ? 'seoaudit-run__pre--wrap' : ''">{{ block.content }}</pre>
                                                            <div v-else class="seoaudit-run__fixbox">
                                                                <span v-for="part in placeholderParts(block.content)" :key="part.key" :class="partClass(part)">{{ part.text }}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </template>
                                                <!-- material was generated for this page, but nothing was derived for this line.
                                                     Only ever claimed when blocks really came back — see the branch below. -->
                                                <span v-else-if="row.belongsToDetail && fixesGenerated && fixesBlocks.length" class="seoaudit-run__recohint">
                                                    No ready-to-paste material for this recommendation.
                                                </span>
                                                <!-- fixes from before the material blocks existed: material was never attempted
                                                     for this line, so the same regenerate wording as on the card applies -->
                                                <span v-else-if="row.belongsToDetail && regenerateHint" class="seoaudit-run__recohint">
                                                    Regenerate to get ready-to-paste material for this recommendation.
                                                </span>
                                                <!-- multi-URL runs aggregate recommendations of every page, material belongs to one -->
                                                <span v-else-if="isMultiUrl && detail && !row.belongsToDetail" class="seoaudit-run__recohint">Open this URL to generate material.</span>
                                            </div>
                                            <v-btn icon x-small class="ml-auto seoaudit-run__recocopy" @click="copyToClipboard(row.text, 'Recommendation copied')" aria-label="Copy recommendation">
                                                <v-icon small color="#007a68">mdi-content-copy</v-icon>
                                            </v-btn>
                                        </div>
                                        <p v-if="materialListCaption" class="q-muted seoaudit-run__recocaption">{{ materialListCaption }}</p>
                                    </template>
                                </template>

                                <!-- PASSED + NOT MEASURED CHECKS (single collapsed expander) -->
                                <div v-if="allPassedChecks.length || notMeasuredChecks.length" class="seoaudit-run__passedwrap">
                                    <span class="seoaudit-run__passedtoggle" @click="passedOpen = !passedOpen">
                                        <v-icon small color="#007a68">mdi-check-circle</v-icon>
                                        {{ passedOpen ? "Hide check details" : passedToggleLabel }}
                                    </span>
                                    <template v-if="passedOpen">
                                        <div v-if="allPassedChecks.length" class="seoaudit-run__passedgrid">
                                            <div v-for="(check, i) in allPassedChecks" :key="'pass-' + i + '-' + check.id" class="seoaudit-run__passeditem">
                                                <span class="q-primary-text">✓</span>
                                                <span>{{ check.label }}</span>
                                                <span class="seoaudit-run__passedvalue">{{ check.details }}</span>
                                            </div>
                                        </div>
                                        <!-- status "info": optional signals and analyses that could not run — no checkmark -->
                                        <template v-if="notMeasuredChecks.length">
                                            <span class="q-eyebrow seoaudit-run__passedsubhead">Not measured</span>
                                            <div class="seoaudit-run__passedgrid">
                                                <div v-for="(check, i) in notMeasuredChecks" :key="'info-' + i + '-' + check.id" class="seoaudit-run__passeditem seoaudit-run__passeditem--muted">
                                                    <v-icon small color="#3d6d66">mdi-minus-circle-outline</v-icon>
                                                    <span>{{ check.label }}</span>
                                                    <span class="seoaudit-run__passedvalue">{{ check.details }}</span>
                                                </div>
                                            </div>
                                        </template>
                                    </template>
                                </div>
                            </template>
                        </v-card-text>
                    </v-card>

                    <!-- READY-TO-USE FIXES (generated on demand, never during the audit run) — directly below
                         the recommendations it belongs to, and only for the material that no recommendation
                         claimed: generated HTML tables and JSON-LD need this width, not the 320px side column -->
                    <v-card v-if="showFixesCard" class="rounded_card quaro-shadow seoaudit-run__sidecard seoaudit-run__fixescard">
                        <v-card-text>
                            <div class="seoaudit-run__sidehead">
                                <!-- multi-URL runs stack the columns: this per-URL card can sit above the table
                                     that says which URL is open, so it names that URL itself -->
                                <span class="q-eyebrow">{{ fixesCardEyebrow }}</span>
                                <span v-if="fixesGenerated && fixesCardBlockCount" class="q-pill q-pill--outline q-pill--primary ml-auto">{{ fixesCardBlockCount }}</span>
                            </div>

                            <!-- NOT GENERATED YET -->
                            <template v-if="!fixesGenerated">
                                <p class="q-muted seoaudit-run__fixesintro">Generate copy-paste-ready title, description, intro, FAQ and schema based on this page.</p>
                                <v-btn class="q-btn q-btn--primary" depressed rounded color="#007a68" dark :disabled="fixesGenerating || !$store.getters.canWriteAiSuite" @click="generateFixSuggestions(false)">
                                    <v-progress-circular v-if="fixesLoading" indeterminate size="16" width="2" color="#ffffff" class="mr-2"></v-progress-circular>
                                    <v-icon v-else small left>mdi-auto-fix</v-icon>
                                    {{ fixesLoading ? "Generating…" : "Generate fix suggestions" }}
                                </v-btn>
                            </template>

                            <!-- GENERATED: only the blocks that came back are rendered -->
                            <template v-else>
                                <div v-for="block in fixTextBlocks" :key="block.key" class="seoaudit-run__fixblock">
                                    <div class="seoaudit-run__fixhead">
                                        <span class="q-eyebrow">{{ block.label }}</span>
                                        <span v-if="block.range" class="seoaudit-run__charcount" :class="charCountClass(block)">{{ block.text.length }} characters</span>
                                        <v-btn icon x-small class="ml-auto" @click="copyToClipboard(block.text, block.label + ' copied')" :aria-label="'Copy ' + block.label">
                                            <v-icon small color="#007a68">mdi-content-copy</v-icon>
                                        </v-btn>
                                    </div>
                                    <div class="seoaudit-run__fixbox">
                                        <span v-for="part in placeholderParts(block.text)" :key="part.key" :class="partClass(part)">{{ part.text }}</span>
                                    </div>
                                </div>

                                <!-- FAQ -->
                                <div v-if="fixesFaq.length" class="seoaudit-run__fixblock">
                                    <div class="seoaudit-run__fixhead">
                                        <span class="q-eyebrow">FAQ</span>
                                        <v-btn icon x-small class="ml-auto" @click="copyToClipboard(faqPlainText, 'FAQ copied')" aria-label="Copy FAQ">
                                            <v-icon small color="#007a68">mdi-content-copy</v-icon>
                                        </v-btn>
                                    </div>
                                    <div class="seoaudit-run__fixbox">
                                        <div v-for="entry in fixesFaq" :key="entry.key" class="seoaudit-run__faqitem">
                                            <div class="seoaudit-run__faqhead">
                                                <span class="font-weight-bold">{{ entry.question }}</span>
                                                <span v-if="entry.outline" class="seoaudit-run__chip">Outline</span>
                                            </div>
                                            <div v-if="entry.answer" class="seoaudit-run__faqanswer">
                                                <span v-for="part in placeholderParts(entry.answer)" :key="part.key" :class="partClass(part)">{{ part.text }}</span>
                                            </div>
                                            <span v-if="entry.outline" class="seoaudit-run__faqhint">Outline only — the facts still have to be filled in.</span>
                                        </div>
                                    </div>
                                </div>

                                <!-- SCHEMA -->
                                <div v-if="fixesSchema" class="seoaudit-run__fixblock">
                                    <div class="seoaudit-run__fixhead">
                                        <span class="q-eyebrow">Schema.org JSON-LD</span>
                                        <v-btn icon x-small class="ml-auto" @click="copyToClipboard(fixesSchema, 'JSON-LD copied')" aria-label="Copy JSON-LD">
                                            <v-icon small color="#007a68">mdi-content-copy</v-icon>
                                        </v-btn>
                                    </div>
                                    <!-- plain text on purpose: JSON arrays look like placeholders and would swallow the real ones -->
                                    <pre class="seoaudit-run__pre">{{ fixesSchema }}</pre>
                                </div>

                                <!-- FACTS -->
                                <div v-if="fixesFacts.length" class="seoaudit-run__fixblock">
                                    <div class="seoaudit-run__fixhead">
                                        <span class="q-eyebrow">Facts to add</span>
                                        <v-btn icon x-small class="ml-auto" @click="copyToClipboard(fixesFacts.join('\n'), 'Facts copied')" aria-label="Copy facts to add">
                                            <v-icon small color="#007a68">mdi-content-copy</v-icon>
                                        </v-btn>
                                    </div>
                                    <ul class="seoaudit-run__factlist">
                                        <li v-for="(fact, i) in fixesFacts" :key="'fact-' + i">
                                            <span v-for="part in placeholderParts(fact)" :key="part.key" :class="partClass(part)">{{ part.text }}</span>
                                        </li>
                                    </ul>
                                </div>

                                <!-- GENERATED MATERIAL: only the blocks no recommendation claimed — everything
                                     that matched a recommendation is rendered inline under that recommendation -->
                                <div v-for="block in unboundFixesBlocks" :key="block.key" class="seoaudit-run__fixblock">
                                    <div class="seoaudit-run__fixhead">
                                        <span class="font-weight-bold seoaudit-run__blocktitle">{{ block.title }}</span>
                                        <span class="seoaudit-run__chip seoaudit-run__blockkind">{{ block.kindLabel }}</span>
                                        <v-btn icon x-small class="ml-auto" @click="copyToClipboard(block.content, block.title + ' copied')" :aria-label="'Copy ' + block.title">
                                            <v-icon small color="#007a68">mdi-content-copy</v-icon>
                                        </v-btn>
                                    </div>
                                    <span v-if="block.note" class="seoaudit-run__blocknote">{{ block.note }}</span>
                                    <!-- markup keeps its own line breaks and stays TEXT on purpose — interpolation only,
                                         never v-html: it is copy-paste material, and its attributes and JSON arrays
                                         look like placeholders and would swallow the real ones.
                                         HTML wraps (long one-line snippets), JSON-LD keeps its indentation and scrolls. -->
                                    <pre v-if="block.preformatted" class="seoaudit-run__pre" :class="block.wraps ? 'seoaudit-run__pre--wrap' : ''">{{ block.content }}</pre>
                                    <div v-else class="seoaudit-run__fixbox">
                                        <span v-for="part in placeholderParts(block.content)" :key="part.key" :class="partClass(part)">{{ part.text }}</span>
                                    </div>
                                    <span v-if="block.recommendation" class="seoaudit-run__blockreco">Realizes: {{ block.recommendation }}</span>
                                </div>

                                <!-- NOTHING NEEDED (the card is hidden when the material only moved to the
                                     recommendations, so an empty card really means nothing was derived) -->
                                <p v-if="!fixesCardBlockCount" class="q-muted seoaudit-run__fixesintro">{{ fixesEmptyText }}</p>

                                <div class="seoaudit-run__fixesactions">
                                    <v-btn v-if="fixBlockCount" small rounded outlined color="#007a68" @click="copyAllFixes()"> <v-icon small left>mdi-content-copy</v-icon>Copy all </v-btn>
                                    <v-btn small text class="q-btn--ghost" :disabled="fixesGenerating || !$store.getters.canWriteAiSuite" @click="generateFixSuggestions(true)">
                                        <v-progress-circular v-if="fixesLoading" indeterminate size="14" width="2" color="#3d6d66" class="mr-2"></v-progress-circular>
                                        {{ fixesLoading ? "Generating…" : "Regenerate" }}
                                    </v-btn>
                                    <!-- fixes from before the material blocks existed: the cached result stays as it is -->
                                    <span v-if="regenerateHint" class="seoaudit-run__regeneratehint">{{ regenerateHint }}</span>
                                </div>
                                <span v-if="fixesGeneratedAt" class="seoaudit-run__fixescaption">Generated {{ formatDate(fixesGeneratedAt) }} · always review before publishing.</span>
                            </template>
                        </v-card-text>
                    </v-card>

                </div>

                <div v-show="activeTab === 'coverage'" class="seoaudit-run__panel" role="tabpanel" id="audit-panel-coverage" aria-labelledby="audit-tab-coverage" tabindex="0">
                    <!-- COVERAGE: focus topics (set on the run) and/or the prompts and fan-out
                         queries of a linked AI project — only the halves that exist are rendered -->
                    <v-card v-if="showCoverageCard" class="rounded_card quaro-shadow seoaudit-run__geocard">
                        <v-card-text>
                            <div class="seoaudit-run__actionshead">
                                <h3 class="mb-0">{{ coverageCardTitle }}</h3>
                                <!-- disclosure instead of a hover tooltip: reachable by keyboard, readable by screen readers -->
                                <button type="button" class="seoaudit-run__infobtn ml-2" :aria-expanded="coverageHelpOpen ? 'true' : 'false'" aria-controls="audit-coverage-help" @click="coverageHelpOpen = !coverageHelpOpen">
                                    <v-icon small color="#3d6d66" aria-hidden="true">mdi-information-outline</v-icon>
                                    <span>How to read this</span>
                                </button>
                                <span v-if="hasProject" class="q-pill q-pill--outline q-pill--primary ml-3">AI project</span>
                            </div>
                            <p class="seoaudit-run__geointro mb-0 mt-1">{{ coverageIntro }}</p>
                            <p class="seoaudit-run__geoexplain mb-0">{{ coverageExplain }}</p>
                            <div v-show="coverageHelpOpen" id="audit-coverage-help" class="seoaudit-run__help">
                                <p class="seoaudit-run__tipblock mb-0">
                                    <strong>Content fit</strong> (the percentages and verdicts): an AI model read this page and judged for every topic whether an assistant could answer it citing this content. It is a
                                    statement about the page, not about rankings.
                                </p>
                                <p class="seoaudit-run__tipblock mb-0">
                                    <strong>Measured</strong> (next to each topic in the lists below): the AI answers your project tracked for that prompt, how many cite this URL as a source and how many mention your
                                    brand. Only tracked prompts carry answers.
                                </p>
                                <p class="seoaudit-run__tipblock mb-0">Google rankings are not part of this audit.</p>
                            </div>

                            <v-row class="mt-1">
                                <!-- focus topics: rendered whenever the run carried focus entries -->
                                <v-col v-if="showFocusStat" cols="12" :sm="coverageStatCols">
                                    <div class="seoaudit-run__geostat">
                                        <span class="q-eyebrow">Focus topics · content fit</span>
                                        <span class="seoaudit-run__geoscore" :style="{ color: scoreColorHex(coverageStats.focusScore) }">
                                            {{ coverageStats.focusScore === null ? "-" : coverageStats.focusScore + "%" }}
                                        </span>
                                        <span class="seoaudit-run__geocaption">{{ focusCoverageCaption }}</span>
                                        <span v-if="focusCoverageHint" class="seoaudit-run__geohint">{{ focusCoverageHint }}</span>
                                    </div>
                                </v-col>
                                <!-- tracked prompts / fan-out only exist with a linked AI project -->
                                <v-col v-if="showPromptStat" cols="12" :sm="coverageStatCols">
                                    <div class="seoaudit-run__geostat">
                                        <span class="q-eyebrow">Tracked prompts · content fit</span>
                                        <span v-if="promptsNotRelevant" class="seoaudit-run__geonotrelevant">{{ notRelevantLabel }}</span>
                                        <span v-else class="seoaudit-run__geoscore" :style="{ color: scoreColorHex(coverageStats.promptScore) }">
                                            {{ coverageStats.promptScore === null ? "-" : coverageStats.promptScore + "%" }}
                                        </span>
                                        <span class="seoaudit-run__geocaption">{{ promptCoverageCaption }}</span>
                                        <span v-if="promptCoverageHint" class="seoaudit-run__geohint">{{ promptCoverageHint }}</span>
                                    </div>
                                </v-col>
                                <v-col v-if="showFanoutStat" cols="12" :sm="coverageStatCols">
                                    <div class="seoaudit-run__geostat">
                                        <span class="q-eyebrow">AI sub-questions (fan-out)</span>
                                        <span v-if="fanoutNotRelevant" class="seoaudit-run__geonotrelevant">{{ notRelevantLabel }}</span>
                                        <span v-else class="seoaudit-run__geoscore" :style="{ color: scoreColorHex(coverageStats.fanoutScore) }">
                                            {{ coverageStats.fanoutScore === null ? "-" : coverageStats.fanoutScore + "%" }}
                                        </span>
                                        <span class="seoaudit-run__geocaption">{{ fanoutCoverageCaption }}</span>
                                        <span v-if="fanoutCoverageHint" class="seoaudit-run__geohint">{{ fanoutCoverageHint }}</span>
                                    </div>
                                </v-col>
                            </v-row>

                            <!-- CONTENT GAPS -->
                            <template v-if="coverageGaps.length">
                                <div class="seoaudit-run__gapshead">
                                    <h4 class="mb-0">Content gaps to close</h4>
                                    <v-btn class="ml-auto" small rounded outlined color="#007a68" @click="copyContentOutline()">
                                        <v-icon small left>mdi-content-copy</v-icon>Copy content outline
                                    </v-btn>
                                </div>
                                <ul class="seoaudit-run__gaplist">
                                <li v-for="gap in visibleCoverageGaps" :key="gap.key" class="seoaudit-run__gap">
                                    <div class="seoaudit-run__gaptags">
                                        <span class="q-pill q-pill--block" :class="gapTypeClass(gap)">{{ gapTypeLabel(gap.type) }}</span>
                                        <!-- focus keeps its own chip colour, so the verdict needs its own chip -->
                                        <span v-if="gap.type === 'focus'" class="seoaudit-run__chip" :class="gapVerdictClass(gap)">{{ gapVerdictLabel(gap) }}</span>
                                        <span v-if="gap.type === 'fanout' && gap.uses > 1" class="seoaudit-run__chip">{{ gap.uses }}x</span>
                                    </div>
                                    <div class="seoaudit-run__gapbody">
                                        <span class="font-weight-bold">{{ gap.text }}</span>
                                        <span v-if="gap.gap" class="seoaudit-run__gaptext">{{ gap.gap }}</span>
                                    </div>
                                </li>
                                </ul>
                                <button v-if="coverageGaps.length > gapLimit" type="button" class="seoaudit-run__expandtoggle" :aria-expanded="coverageGapsOpen ? 'true' : 'false'" @click="coverageGapsOpen = !coverageGapsOpen">
                                    {{ coverageGapsOpen ? "Show fewer gaps" : "Show all " + coverageGaps.length + " gaps" }}
                                    <span aria-hidden="true">{{ coverageGapsOpen ? "▴" : "▾" }}</span>
                                </button>
                            </template>
                            <!-- "no gaps" is only good news when something was analyzed at all;
                                 if every half came back empty the stats above already explain why -->
                            <div v-else-if="coverageAnalyzed" class="seoaudit-run__emptystate mt-4">
                                <v-icon color="#007a68" size="42" class="mr-3">mdi-check-decagram</v-icon>
                                <span>{{ coverageEmptyText }}</span>
                            </div>
                        </v-card-text>
                    </v-card>

                    <!-- FOCUS TOPICS: every topic the user set, covered ones included, with the passage
                         that would be cited and (with a project) the measured answers next to it -->
                    <coverage-focus
                        v-if="hasRunFocus"
                        :key="'cf-' + (detail && detail._id ? detail._id : 'run')"
                        :run="run"
                        :result="detail && detail.status === 'done' ? detail : null"
                        :merged-focus="mergedCoverage.focus"
                        :project-id="projectIdString"
                        :tracking="trackingByKey"
                        :tracking-loading="trackingLoading"
                        :tracking-days="trackingDays"
                        :tracking-error="trackingError"
                        :is-multi-url="isMultiUrl" />

                    <!-- PROMPTS CHECKED: per opened page — pick them by hand or let the audit choose -->
                    <coverage-prompts
                        v-if="hasProject && detail && detail.status === 'done'"
                        :key="'cp-' + detail._id"
                        :run="run"
                        :result="detail"
                        :company-id="companyId"
                        :tracking="trackingByKey"
                        :tracking-loading="trackingLoading"
                        :tracking-days="trackingDays"
                        :tracking-error="trackingError"
                        @updated="onCoverageUpdated" />
                    <v-card v-else-if="hasProject && isMultiUrl && showCoverageCard" class="rounded_card quaro-shadow mt-5">
                        <v-card-text>
                            <p class="q-muted mb-0">Open a URL in the URLs tab to see and change the prompts checked for that page.</p>
                        </v-card-text>
                    </v-card>

                    <!-- PROMPT GAP: who gets cited instead of the opened page, and why -->
                    <gap-analysis v-if="hasProject && detail && detail.status === 'done'" :key="'gap-' + detail._id" :run="run" :result="detail" :company-id="companyId" />
                </div>

                <div v-show="activeTab === 'impact'" class="seoaudit-run__panel" role="tabpanel" id="audit-panel-impact" aria-labelledby="audit-tab-impact" tabindex="0">
                    <!-- IMPACT: audit history of the opened page against its citations -->
                    <impact-card v-if="detail && detail.status === 'done'" :key="'impact-' + detail._id" :run="run" :result="detail" :company-id="companyId" />
                    <v-card v-else class="rounded_card quaro-shadow">
                        <v-card-text>
                            <p class="q-muted mb-0">Select a URL in the URLs tab to see its audit history and citations.</p>
                        </v-card-text>
                    </v-card>
                </div>

                <div v-show="activeTab === 'page'" class="seoaudit-run__panel" role="tabpanel" id="audit-panel-page" aria-labelledby="audit-tab-page" tabindex="0">
                    <!-- PER-URL TABLE (multi-URL runs) -->
                    <v-card v-if="isMultiUrl && summary.length" class="rounded_card quaro-shadow">
                        <v-data-table
                            :headers="urlHeaders"
                            :items="summary"
                            :items-per-page="50"
                            :footer-props="{ 'items-per-page-options': [10, 20, 50] }"
                            class="elevation-0 seoaudit-run__urltable"
                            @click:row="openResult">
                            <template v-slot:item.url="{ item }">
                                <!-- the row click is mouse-only; the URL itself is a real button for keyboard users -->
                                <button type="button" class="seoaudit-run__url seoaudit-run__urlbtn" @click.stop="openResult(item)">{{ item.url }}</button>
                            </template>
                            <template v-slot:item.total="{ item }">
                                <span v-if="item.scores" class="q-pill q-pill--block" :style="{ background: scoreColorHex(item.scores.total) }">{{ item.scores.total }}</span>
                                <span v-else class="q-pill q-pill--outline q-pill--danger">{{ item.status }}</span>
                            </template>
                            <template v-slot:item.issues="{ item }">
                                <span v-if="!urlIssues(item)" class="q-muted">—</span>
                                <span v-else-if="urlIssues(item).total" class="q-pill q-pill--block" :class="urlIssues(item).fail ? 'q-pill--danger' : 'q-pill--warn'">{{
                                    urlIssues(item).total
                                }}</span>
                                <span v-else class="q-pill q-pill--outline q-pill--primary">0</span>
                            </template>
                            <template v-slot:item.technical="{ item }">
                                <span v-if="categoryScore(item, 'technical') !== null" class="seoaudit-run__cellscore" :style="{ color: scoreColorHex(categoryScore(item, 'technical')) }">
                                    {{ categoryScore(item, "technical") }}
                                </span>
                                <span v-else class="q-muted">—</span>
                            </template>
                            <template v-slot:item.structure="{ item }">
                                <span v-if="categoryScore(item, 'structure') !== null" class="seoaudit-run__cellscore" :style="{ color: scoreColorHex(categoryScore(item, 'structure')) }">
                                    {{ categoryScore(item, "structure") }}
                                </span>
                                <span v-else class="q-muted">—</span>
                            </template>
                            <template v-slot:item.machine="{ item }">
                                <span v-if="categoryScore(item, 'machine') !== null" class="seoaudit-run__cellscore" :style="{ color: scoreColorHex(categoryScore(item, 'machine')) }">
                                    {{ categoryScore(item, "machine") }}
                                </span>
                                <span v-else class="q-muted">—</span>
                            </template>
                            <template v-slot:item.citability="{ item }">
                                <span v-if="categoryScore(item, 'citability') !== null" class="seoaudit-run__cellscore" :style="{ color: scoreColorHex(categoryScore(item, 'citability')) }">
                                    {{ categoryScore(item, "citability") }}
                                </span>
                                <span v-else class="q-muted">—</span>
                            </template>
                            <template v-slot:item.js_verdict="{ item }">
                                <span class="q-pill q-pill--block" :class="'q-pill--' + jsChip(item.js_verdict).color">{{ jsChip(item.js_verdict).label }}</span>
                            </template>
                            <template v-slot:item.cited_count="{ item }">
                                <span class="q-pill q-pill--outline q-pill--primary"> <v-icon x-small class="mr-1">mdi-format-quote-close</v-icon>{{ item.cited_count || 0 }} </span>
                            </template>
                            <template v-slot:item.coverage="{ item }">
                                <span v-if="coverageScore(item) !== null" class="q-pill q-pill--block" :style="{ background: scoreColorHex(coverageScore(item)) }">{{ coverageScore(item) }}%</span>
                                <span v-else class="q-muted">—</span>
                            </template>
                        </v-data-table>
                        <div class="seoaudit-run__tablehint">Select a URL to see its checks and generate ready-to-use fixes.</div>
                    </v-card>

                    <!-- OPENED RESULT: one detail stack, identical in single- and multi-URL runs -->
                    <template v-if="detail">
                        <!-- CHECKED PAGE -->
                        <v-card class="rounded_card quaro-shadow seoaudit-run__sidecard">
                            <v-card-text>
                                <div class="seoaudit-run__sidehead">
                                    <span class="q-eyebrow">Checked page</span>
                                    <v-btn v-if="isMultiUrl" icon x-small class="ml-auto" @click="closeDetail()" aria-label="Close page details">
                                        <v-icon small color="#3d6d66">mdi-close</v-icon>
                                    </v-btn>
                                </div>
                                <div class="seoaudit-run__pageurl">{{ detail.url }}</div>
                                <div v-if="detailFactsPer100 !== null" class="seoaudit-run__detailchips">
                                    <span class="seoaudit-run__chip" :class="factsChipClass(detailFactsPer100)">{{ detailFactsPer100 }} facts / 100 words</span>
                                </div>
                                <div class="seoaudit-run__divider"></div>
                                <div class="seoaudit-run__signal">
                                    <span class="seoaudit-run__dot" :class="'q-dot--' + jsChip(detailJsVerdict).color"></span>
                                    <span>{{ jsChip(detailJsVerdict).label }}</span>
                                </div>
                                <div class="seoaudit-run__signal" v-if="detail.scores">
                                    <span class="seoaudit-run__dot q-dot--primary"></span>
                                    <span>Score {{ detail.scores.total }} of 100</span>
                                </div>
                                <div class="seoaudit-run__signal" v-if="run.project_id">
                                    <span class="seoaudit-run__dot" :class="detailCitedCount ? 'q-dot--primary' : 'q-dot--warn'"></span>
                                    <span>{{ detailCitedCount }} citation{{ detailCitedCount === 1 ? "" : "s" }} in AI answers</span>
                                </div>
                            </v-card-text>
                        </v-card>

                        <!-- PASSAGE QUALITY moves to its own tab: 15 stacked passage cards were the
                             single longest block on the page -->
                    </template>
                </div>

                <div v-show="activeTab === 'passages'" class="seoaudit-run__panel" role="tabpanel" id="audit-panel-passages" aria-labelledby="audit-tab-passages" tabindex="0">
                    <template v-if="detail">
                        <!-- PASSAGE QUALITY (per-section LLM analysis of the opened page) -->
                        <v-card v-if="analyzedSections.length" class="rounded_card quaro-shadow seoaudit-run__sidecard seoaudit-run__passagescard">
                            <v-card-text>
                                <div class="seoaudit-run__sidehead">
                                    <span class="q-eyebrow">Passage quality</span>
                                    <span class="font-weight-bold ml-auto" :class="problemSections.length ? 'seoaudit-run__passagecount' : 'q-primary-text'">{{ problemSections.length }}</span>
                                </div>
                                <div v-for="section in problemSections" :key="'section-' + section.index" class="seoaudit-run__passage">
                                    <div class="seoaudit-run__passagehead">
                                        <span class="font-weight-bold">{{ sectionHeading(section) }}</span>
                                        <span v-if="section.word_count" class="seoaudit-run__passagewords">{{ section.word_count }} words</span>
                                    </div>
                                    <div class="seoaudit-run__passagetags">
                                        <span v-for="tag in sectionProblems(section)" :key="'section-' + section.index + '-' + tag.label" class="seoaudit-run__chip" :class="tag.cls">{{
                                            tag.label
                                        }}</span>
                                    </div>
                                    <div v-if="section.issue" class="seoaudit-run__passageissue">{{ section.issue }}</div>
                                    <div v-if="section.suggested_opening" class="seoaudit-run__suggestion">
                                        <div class="seoaudit-run__suggestionhead">
                                            <span class="q-eyebrow">Suggested opening:</span>
                                            <v-btn icon x-small class="ml-auto" @click="copyToClipboard(section.suggested_opening, 'Suggested opening copied')" aria-label="Copy suggested opening">
                                                <v-icon small color="#007a68">mdi-content-copy</v-icon>
                                            </v-btn>
                                        </div>
                                        <span class="seoaudit-run__suggestiontext">{{ section.suggested_opening }}</span>
                                    </div>
                                </div>
                                <div v-if="wellStructuredCount" class="seoaudit-run__passageok">
                                    <v-icon small color="#007a68" class="mr-1">mdi-check-circle</v-icon>
                                    <span>{{ wellStructuredCount }} of {{ analyzedSections.length }} passages are well structured.</span>
                                </div>
                            </v-card-text>
                        </v-card>

                        <!-- QUOTABLE PASSAGES -->
                        <v-card v-if="detail.llm && detail.llm.quotable_passages && detail.llm.quotable_passages.length" class="rounded_card quaro-shadow seoaudit-run__sidecard">
                            <v-card-text>
                                <div class="seoaudit-run__sidehead">
                                    <span class="q-eyebrow">Quotable passages</span>
                                    <span class="q-primary-text font-weight-bold ml-auto">{{ detail.llm.quotable_passages.length }}</span>
                                </div>
                                <div v-for="(passage, i) in detail.llm.quotable_passages" :key="'q' + i" class="seoaudit-run__quote">
                                    <div class="seoaudit-run__quotetext">„{{ passage.text }}"</div>
                                    <div class="seoaudit-run__quotereason">{{ passage.reason }}</div>
                                </div>
                            </v-card-text>
                        </v-card>

                        <!-- AI ANALYSIS MISSING FOR THE OPENED PAGE -->
                        <v-card v-if="!hasLlm" class="rounded_card quaro-shadow seoaudit-run__sidecard">
                            <v-card-text>
                                <v-alert type="info" text class="mb-0">AI analysis is not available for this URL.</v-alert>
                            </v-card-text>
                        </v-card>
                    </template>
                </div>
            </div>
        </template>

        <!-- NOT FOUND -->
        <v-alert v-else type="error" text class="mt-10"> This audit could not be loaded. </v-alert>
    </div>
</template>

<script>
    import { fetchRun, fetchResults, createRun, generateFixes, fetchCoverageTracking } from "../../components/seoaudit/seoAuditApi"
    import GapAnalysis from "../../components/seoaudit/GapAnalysis.vue"
    import ImpactCard from "../../components/seoaudit/ImpactCard.vue"
    import CoveragePrompts from "../../components/seoaudit/CoveragePrompts.vue"
    import CoverageFocus from "../../components/seoaudit/CoverageFocus.vue"
    import { coverageKey } from "../../components/seoaudit/coverageText"

    const CATEGORY_META = [
        { key: "technical", label: "Technical" },
        { key: "structure", label: "Structure" },
        { key: "machine", label: "Machine-readable" },
        { key: "citability", label: "Citability" }
    ]

    // results saved before the backend added check.impact fall back to this value
    const IMPACT_FALLBACK = 5
    const PRIORITY_RANK = { high: 0, medium: 1, low: 2 }
    // coverage verdicts, best verdict wins when the same prompt/query is judged on several URLs
    const VERDICT_RANK = { missing: 0, partial: 1, covered: 2 }
    // rows of the content gap list shown before the "Show all" toggle
    // overview "Ready to paste" card: one line per block, previews cut to a single line
    const OVERVIEW_MATERIAL_LIMIT = 6
    const OVERVIEW_PREVIEW_CHARS = 110
    const GAP_LIMIT = 12
    // the Impact tab (audit history against citations) is hidden for now; its panel and
    // component stay in place so the tab can come back by flipping this flag
    const SHOW_IMPACT_TAB = false
    // recommended lengths of the generated title / meta description (backend uses the same ranges)
    const FIX_TITLE_RANGE = { min: 15, max: 65 }
    const FIX_META_RANGE = { min: 50, max: 165 }
    // ready-to-paste material blocks: allowed kinds and their UI labels. Anything else the
    // generator sends is shown as plain text (the backend defaults unknown kinds to "text" too).
    const FIX_BLOCK_KIND_LABELS = { text: "Text", markdown: "Markdown", html: "HTML", jsonld: "JSON-LD" }
    const FIX_BLOCK_KIND_FALLBACK = "text"
    // placeholders the generator leaves for facts it must not invent. Shape per generator prompt:
    // "[KEYWORD: what is missing]" — an uppercase keyword, a colon, a one-line description
    // ("[ADD: current price]" / "[ERGÄNZEN: aktueller Preis]").
    // Split candidates: one line, no braces/quotes (those belong to JSON-LD or markup) and not the
    // label of a markdown link "[ASFINAG](https://…)". Everything else stays one plain part.
    const PLACEHOLDER_SPLIT = /(\[[^\][{}"\n]+\](?!\())/
    // A candidate only counts as a placeholder with an uppercase keyword in front of the colon.
    // Ordinary markdown material — link labels, footnote refs "[1]", "[Quelle: ASFINAG]" — is
    // material, not a to-do, and must never be marked as "still to be filled in".
    const isPlaceholderPart = (part) => {
        if (typeof part !== "string" || part.length < 4) return false
        if (part.charAt(0) !== "[" || part.charAt(part.length - 1) !== "]") return false
        const inner = part.slice(1, -1)
        const colon = inner.indexOf(":")
        if (colon <= 0) return false
        const keyword = inner.slice(0, colon).trim()
        const description = inner.slice(colon + 1).trim()
        if (!keyword || !description) return false
        // script-independent uppercase test: "ERGÄNZEN"/"ДОБАВИТЬ" stay themselves when uppercased
        // and change when lowercased, while "Quelle", "https" or "1" fail one of the two
        return keyword === keyword.toUpperCase() && keyword !== keyword.toLowerCase()
    }
    // FAQ entries the generator could only outline must never be pasted as if they were answers
    const OUTLINE_MARKER = " [OUTLINE - not answered on the page, facts still missing]"
    const REVIEW_WARNING = "Always review before publishing: placeholders in [brackets] and outline entries still need real facts."
    // backend markers of the fix generation endpoint -> readable text. The marker is required:
    // a proxy-generated 502/504 without it must not be reported as a page or timeout problem.
    const FIX_ERROR_MESSAGES = [
        { status: 502, error: "page could not be fetched", message: "The page could not be fetched for fix generation." },
        { status: 409, error: "result has no successful audit", message: "This page has no successful audit result, so there is nothing to generate fixes from." },
        { status: 409, error: "fix generation already running", message: "Fix generation is already running for this page. Please wait a moment and try again." },
        { status: 504, error: "fix generation timed out", message: "Fix generation timed out. Please try again in a moment." }
    ]
    const FIX_ERROR_FALLBACK = "Fix generation failed. Please try again later."

    export default {
        name: "SeoAuditRun",
        components: { GapAnalysis, ImpactCard, CoveragePrompts, CoverageFocus },
        data() {
            return {
                loading: true,
                run: null,
                summary: [],
                notification: { show: false },
                pollInterval: null,
                // set in beforeDestroy so in-flight requests cannot restart polling afterwards
                isDestroyed: false,
                // full result docs, loaded once when the run is done
                results: [],
                resultsLoading: false,
                resultsLoaded: false,
                // per-action expanded state, keyed by check id
                expandedActions: {},
                // per-recommendation state of the inline material panel, keyed by recommendation row key.
                // Only rows the user toggled appear here; everything else follows the default (collapsed,
                // except a single recommendation that owns all the material).
                expandedRecoMaterial: {},
                detail: null,
                detailLoading: false,
                // category tile click filters the action list (single or multi URL)
                activeCategory: null,
                // tabbed workspaces: overview | fixes | coverage | passages | page
                activeTab: "overview",
                passedOpen: false,
                // true while a re-check is starting a new run
                reRunning: false,
                // GEO coverage: content gap list is capped until the user expands it
                coverageGapsOpen: false,
                gapLimit: GAP_LIMIT,
                // id of the result whose fixes are currently being generated (null = nothing running).
                // A run can show several URLs, so the spinner must follow the generating URL, not the shown one.
                fixesLoadingId: null,
                // bumped on every generation start and on run navigation; a response with a stale
                // token belongs to an abandoned request and is ignored
                fixesRequestToken: 0,
                // measured visibility of the opened page (POST /coverage/tracking): answers,
                // citations of this URL and brand mentions per focus topic / checked prompt
                tracking: null,
                trackingLoading: false,
                trackingError: "",
                trackingToken: 0,
                // "How to read this" disclosure on the coverage card
                coverageHelpOpen: false
            }
        },
        computed: {
            isActive() {
                return this.run && (this.run.status === "pending" || this.run.status === "processing")
            },
            progressTotal() {
                return this.run && this.run.progress && this.run.progress.total ? this.run.progress.total : 0
            },
            progressDone() {
                return this.run && this.run.progress && this.run.progress.done ? this.run.progress.done : 0
            },
            progressPercent() {
                return this.progressTotal ? Math.round((this.progressDone / this.progressTotal) * 100) : 0
            },
            // tab bar: counts come from the lists the tabs actually render, so a badge can never
            // promise more than the panel behind it shows. Tabs with an empty panel are dropped.
            visibleTabs() {
                const tabs = [
                    { key: "overview", label: "Overview", count: null },
                    { key: "fixes", label: "Fixes", count: this.resultsLoaded ? this.aggregatedActions.length : null }
                ]
                if (this.showCoverageCard) tabs.push({ key: "coverage", label: "Coverage", count: this.coverageGaps.length })
                if (this.detail && this.problemSections.length) tabs.push({ key: "passages", label: "Passages", count: this.problemSections.length })
                if (SHOW_IMPACT_TAB) tabs.push({ key: "impact", label: "Impact", count: null })
                tabs.push({ key: "page", label: this.isMultiUrl ? "URLs" : "Page", count: this.isMultiUrl ? this.totalUrlCount : null })
                return tabs
            },
            // the three highest-impact fixes, shown on the overview as the entry point
            topActions() {
                return this.aggregatedActions.slice(0, 3)
            },
            totalUrlCount() {
                if (!this.run) return 0
                return this.run.url_count || (this.run.urls ? this.run.urls.length : 0) || this.summary.length
            },
            isMultiUrl() {
                return this.totalUrlCount > 1
            },
            doneResults() {
                return this.results.filter((r) => r.status === "done")
            },
            // comparison with the previous audit of the same URL(s); null when there is none.
            // The backend only compares results of the same scoring version — otherwise it
            // reports comparable:false and claims nothing.
            progressSummary() {
                const rows = this.doneResults.filter((r) => r.progress && r.progress.previous_fetched_at)
                if (!rows.length) return null

                const newest = rows.reduce((latest, r) => (new Date(r.progress.previous_fetched_at) > new Date(latest) ? r.progress.previous_fetched_at : latest), rows[0].progress.previous_fetched_at)
                const dateLabel = "compared with " + this.formatDate(newest)

                const comparable = rows.filter((r) => r.progress.comparable === true && r.progress.score_delta && typeof r.progress.score_delta.total === "number")
                if (!comparable.length) return { comparable: false, dateLabel }

                const delta = Math.round(comparable.reduce((acc, r) => acc + r.progress.score_delta.total, 0) / comparable.length)
                // the AI-judged part of the score moves by a point or so on an unchanged page
                const trend = delta > 1 ? "up" : delta < -1 ? "down" : "flat"
                const urlNote = this.isMultiUrl ? " on average across " + comparable.length + " URL" + (comparable.length === 1 ? "" : "s") : ""
                const deltaText = (trend === "up" ? "Score improved" : trend === "down" ? "Score dropped" : "Score practically unchanged") + urlNote

                const collect = (key) => {
                    const byId = new Map()
                    comparable.forEach((r) => {
                        ;(Array.isArray(r.progress[key]) ? r.progress[key] : []).forEach((entry) => {
                            if (!entry || !entry.id) return
                            const item = byId.get(entry.id) || { id: entry.id, label: entry.label || entry.id, llm_judged: entry.llm_judged === true, count: 0 }
                            item.count++
                            byId.set(entry.id, item)
                        })
                    })
                    return [...byId.values()].map((item) => ({
                        ...item,
                        text: item.label + (item.llm_judged ? " *" : "") + (this.isMultiUrl && item.count > 1 ? " · " + item.count + " URLs" : "")
                    }))
                }
                const resolved = collect("resolved")
                const regressed = collect("regressed")

                return {
                    comparable: true,
                    dateLabel,
                    delta,
                    trend,
                    deltaLabel: (delta > 0 ? "+" : "") + delta,
                    deltaText,
                    resolved,
                    regressed,
                    hasLlmJudged: resolved.concat(regressed).some((entry) => entry.llm_judged)
                }
            },
            categoryTiles() {
                const rows = this.summary.filter((r) => r.scores)
                return CATEGORY_META.map((cat) => {
                    const tile = { ...cat, score: null, failCount: 0, warnCount: 0 }
                    if (rows.length) {
                        const sum = rows.reduce((acc, r) => acc + (r.scores[cat.key] || 0), 0)
                        tile.score = Math.round(sum / rows.length)
                    }
                    this.doneResults.forEach((res) => {
                        ;(res.checks || []).forEach((check) => {
                            if (check.category !== cat.key) return
                            if (check.status === "fail") tile.failCount++
                            else if (check.status === "warn") tile.warnCount++
                        })
                    })
                    return tile
                })
            },
            // fail/warn checks of all URLs grouped by check id, fails first, then impact desc
            aggregatedActions() {
                const groups = {}
                const order = []
                this.doneResults.forEach((res) => {
                    ;(res.checks || []).forEach((check) => {
                        if (check.status !== "fail" && check.status !== "warn") return
                        if (!groups[check.id]) {
                            groups[check.id] = {
                                id: check.id,
                                label: check.label,
                                category: check.category,
                                worstStatus: check.status,
                                impact: this.checkImpact(check),
                                affected: []
                            }
                            order.push(check.id)
                        }
                        const group = groups[check.id]
                        if (check.status === "fail") group.worstStatus = "fail"
                        group.impact = Math.max(group.impact, this.checkImpact(check))
                        group.affected.push({
                            url: res.url,
                            result_id: res._id,
                            details: check.details,
                            recommendation: check.recommendation
                        })
                    })
                })
                return order
                    .map((id) => groups[id])
                    .sort((a, b) => {
                        if (a.worstStatus !== b.worstStatus) return a.worstStatus === "fail" ? -1 : 1
                        return b.impact - a.impact
                    })
            },
            // category-tile click filters this list
            filteredActions() {
                if (!this.activeCategory) return this.aggregatedActions
                return this.aggregatedActions.filter((a) => a.category === this.activeCategory)
            },
            issueCount() {
                return this.aggregatedActions.length
            },
            // sum of impact over all fail/warn checks, deduplicated per check id by max impact
            achievablePoints() {
                return Math.round(this.aggregatedActions.reduce((acc, action) => acc + action.impact, 0))
            },
            // per-URL LLM recommendations across the whole run, high > medium > low
            llmActionRecommendations() {
                const items = []
                this.doneResults.forEach((res) => {
                    if (!res.llm || !Array.isArray(res.llm.recommendations)) return
                    res.llm.recommendations.forEach((reco) => {
                        // the result id travels along: generated material belongs to ONE result, so a row
                        // can only carry material when it comes from the result that is currently open
                        items.push({ priority: reco.priority || "medium", text: reco.text, url: res.url, result_id: res._id })
                    })
                })
                return items.sort((a, b) => this.priorityRank(a.priority) - this.priorityRank(b.priority))
            },
            // the copied list names what it belongs to: the page URL whenever every recommendation
            // comes from the same page, the run name when the list mixes several pages
            recommendationsScope() {
                const urls = []
                this.llmActionRecommendations.forEach((reco) => {
                    if (reco.url && urls.indexOf(reco.url) === -1) urls.push(reco.url)
                })
                if (urls.length === 1) return urls[0]
                return (this.run && this.run.name) || "this audit"
            },
            // plain-text version of the whole recommendation list, ready to paste into a ticket
            recommendationsBriefText() {
                const list = this.llmActionRecommendations
                if (!list.length) return ""
                const lines = ["AI content recommendations for " + this.recommendationsScope, ""]
                list.forEach((reco) => {
                    const text = this.fixText(reco.text)
                    if (!text) return
                    // a multi-URL list mixes pages, so the URL has to travel with its line
                    const source = this.isMultiUrl && reco.url ? " (" + reco.url + ")" : ""
                    lines.push("- " + this.priorityLabel(reco.priority) + ": " + text + source)
                })
                return lines.join("\n")
            },
            // checks that really passed (single collapsed expander, replaces per-detail list)
            allPassedChecks() {
                return this.checksWithStatus("pass")
            },
            // status "info": optional signals and analyses that could not run — no measurement, so no checkmark
            notMeasuredChecks() {
                return this.checksWithStatus("info")
            },
            passedToggleLabel() {
                const passed = this.allPassedChecks.length
                const info = this.notMeasuredChecks.length
                if (!passed) return "Show " + info + " not measured check" + (info === 1 ? "" : "s")
                let label = "Show " + passed + " passed check" + (passed === 1 ? "" : "s")
                if (info) label += " · " + info + " not measured"
                return label
            },
            // fail+warn counts per result id for the Issues column
            issuesByResultId() {
                const map = {}
                this.results.forEach((res) => {
                    if (!Array.isArray(res.checks)) return
                    let fail = 0
                    let warn = 0
                    res.checks.forEach((check) => {
                        if (check.status === "fail") fail++
                        else if (check.status === "warn") warn++
                    })
                    map[String(res._id)] = { fail: fail, warn: warn, total: fail + warn }
                })
                return map
            },
            // ---- coverage (focus topics of the run and/or the prompts/fan-out of a linked project) ----
            coverageResults() {
                return this.doneResults.filter((res) => res && res.coverage && typeof res.coverage === "object")
            },
            hasCoverage() {
                return this.coverageResults.length > 0
            },
            hasProject() {
                return !!(this.run && this.run.project_id)
            },
            // the child components call the audit API themselves
            companyId() {
                return localStorage.company_id || ""
            },
            projectIdString() {
                return this.run && this.run.project_id ? String(this.run.project_id) : ""
            },
            // measured rows keyed like the lists match prompt texts (coverageKey)
            trackingByKey() {
                const map = {}
                const rows = this.tracking && Array.isArray(this.tracking.rows) ? this.tracking.rows : []
                rows.forEach((row) => {
                    const key = coverageKey(row && row.text)
                    if (key && !map[key]) map[key] = row
                })
                return map
            },
            trackingDays() {
                const days = this.tracking && this.toNumber(this.tracking.days)
                return days ? days : 90
            },
            // focus entries live on the run document (sanitized server-side) and exist with or
            // without an AI project — they are the only coverage source a projectless run has
            runFocusEntries() {
                const list = this.run && Array.isArray(this.run.focus) ? this.run.focus : []
                return list.map((entry) => (typeof entry === "string" ? entry.trim() : "")).filter(Boolean)
            },
            hasRunFocus() {
                return this.runFocusEntries.length > 0
            },
            // at least one half returned judged entries
            coverageAnalyzed() {
                const stats = this.coverageStats
                return !!(stats.focusTotal || stats.promptsTotal || stats.fanoutTotal)
            },
            // never render an empty coverage shell when the stored coverage carries no usable data.
            // A coverage document whose halves are all empty is still worth showing when it explains
            // itself: candidates that were filtered away, or focus topics that got no verdict.
            showCoverageCard() {
                if (!this.hasCoverage) return false
                if (this.coverageAnalyzed || this.coverageGaps.length) return true
                return !!(this.promptsNotRelevant || this.fanoutNotRelevant || this.hasRunFocus)
            },
            hasCoverageColumn() {
                return Object.keys(this.coverageByResultId).length > 0
            },
            // headline score, caption and gap list all read the merged prompt/fan-out sets,
            // scored with the backend formula: (covered + 0.5 * partial) / total
            coverageStats() {
                const summarize = (items) => {
                    const stats = { total: items.length, covered: 0, partial: 0, score: null }
                    items.forEach((item) => {
                        if (item.verdict === "covered") stats.covered++
                        else if (item.verdict === "partial") stats.partial++
                    })
                    if (stats.total) stats.score = Math.round(((stats.covered + 0.5 * stats.partial) / stats.total) * 100)
                    return stats
                }
                const focus = summarize(this.mergedCoverage.focus)
                const prompts = summarize(this.mergedCoverage.prompts)
                const fanout = summarize(this.mergedCoverage.fanout)
                return {
                    // focus entries are set on the run and are never relevance-filtered (missing on old results)
                    focusScore: focus.score,
                    focusCovered: focus.covered,
                    focusPartial: focus.partial,
                    focusTotal: focus.total,
                    promptScore: prompts.score,
                    promptsCovered: prompts.covered,
                    promptsPartial: prompts.partial,
                    promptsTotal: prompts.total,
                    // optional backend field: the analysis may only judge a sample of the tracked prompts
                    promptsTracked: this.coverageTrackedPrompts,
                    // optional backend fields: candidates available before the per-URL relevance filter
                    promptsCandidates: this.coverageCandidates.prompts,
                    fanoutCandidates: this.coverageCandidates.fanout,
                    fanoutScore: fanout.score,
                    fanoutCovered: fanout.covered,
                    fanoutPartial: fanout.partial,
                    fanoutTotal: fanout.total
                }
            },
            // coverage.prompts_candidates / fanout_candidates are optional (missing on older
            // results, where relevance filtering did not exist yet) — largest wins, null = unknown
            coverageCandidates() {
                const largest = (key) => {
                    let value = null
                    this.coverageResults.forEach((res) => {
                        const num = this.toNumber((res.coverage || {})[key])
                        if (num === null) return
                        value = value === null ? num : Math.max(value, num)
                    })
                    return value
                }
                return { prompts: largest("prompts_candidates"), fanout: largest("fanout_candidates") }
            },
            // relevance data exists only on results produced after the relevance filter was added
            hasRelevanceData() {
                return this.coverageStats.promptsCandidates !== null || this.coverageStats.fanoutCandidates !== null
            },
            // a half with candidates but nothing analyzed was emptied by the relevance filter —
            // that is "not relevant for this page", never 0%. Old results (candidates unknown)
            // keep the neutral "not analyzed" wording they had before.
            promptsNotRelevant() {
                const stats = this.coverageStats
                return !stats.promptsTotal && stats.promptsCandidates !== null && stats.promptsCandidates > 0
            },
            fanoutNotRelevant() {
                const stats = this.coverageStats
                return !stats.fanoutTotal && stats.fanoutCandidates !== null && stats.fanoutCandidates > 0
            },
            // the focus half belongs to the run, not to a project: it is shown as soon as the user
            // set focus entries, even when the model returned no focus verdict for them
            showFocusStat() {
                return this.coverageStats.focusTotal > 0 || this.hasRunFocus
            },
            // prompts and fan-out queries only exist for runs linked to an AI project — without one
            // both halves would be permanently dead columns
            showPromptStat() {
                return this.hasProject || this.coverageStats.promptsTotal > 0 || this.promptsNotRelevant
            },
            showFanoutStat() {
                return this.hasProject || this.coverageStats.fanoutTotal > 0 || this.fanoutNotRelevant
            },
            coverageStatCols() {
                const visible = [this.showFocusStat, this.showPromptStat, this.showFanoutStat].filter(Boolean).length
                return visible > 1 ? Math.floor(12 / visible) : 12
            },
            // the card is named after what it actually contains
            coverageCardTitle() {
                return this.showPromptStat || this.showFanoutStat ? "GEO coverage" : "Focus topic coverage"
            },
            coverageIntro() {
                if (!this.showPromptStat && !this.showFanoutStat) {
                    return "How well this content answers the focus topics you set for this audit."
                }
                if (this.showFocusStat) {
                    return "How well this content answers your focus topics and the prompts and AI sub-questions of this project that are relevant for these pages."
                }
                if (this.hasRelevanceData) {
                    return "How well this content answers the tracked prompts and AI sub-questions of this project that are relevant for these pages."
                }
                return "How well this content answers the prompts and AI sub-questions tracked for this project."
            },
            // the one sentence that keeps the percentages honest: they describe the page, not the SERPs
            coverageExplain() {
                let text = "The percentages are a content fit: an AI model judged from the page content whether each topic could be answered citing " + this.pageWord + ". They do not say whether you appear for it today."
                if (this.hasProject) text += " What AI assistants actually do is measured per topic in the lists below, from the answers tracked in your AI project."
                else text += " Link the audit to an AI project to also see citations and brand mentions per topic."
                return text
            },
            coverageEmptyText() {
                if (!this.showPromptStat && !this.showFanoutStat) return "No gaps found in the analyzed focus topics."
                if (this.showFocusStat) return "No gaps found in the analyzed focus topics, prompts and sub-questions."
                return "No gaps found in the analyzed prompts and sub-questions."
            },
            // one run can cover several URLs — every per-page statement has to say so
            notRelevantLabel() {
                return "Not relevant for " + this.pageWord
            },
            pageWord() {
                return this.isMultiUrl ? "these pages" : "this page"
            },
            // coverage.prompts_tracked_total is optional (missing on older results) — largest wins
            coverageTrackedPrompts() {
                let tracked = null
                this.coverageResults.forEach((res) => {
                    const value = this.toNumber((res.coverage || {}).prompts_tracked_total)
                    if (value === null) return
                    tracked = tracked === null ? value : Math.max(tracked, value)
                })
                return tracked
            },
            focusCoverageCaption() {
                const stats = this.coverageStats
                // focus entries are never relevance-filtered, so an empty half means the model
                // returned no verdict for them — the same "not analyzed" state the other halves have
                if (!stats.focusTotal) return "Not analyzed for this audit."
                let caption = stats.focusCovered + " of " + stats.focusTotal + " focus topic" + (stats.focusTotal === 1 ? "" : "s") + " answered on " + this.pageWord
                if (stats.focusPartial) caption += " (" + stats.focusPartial + " partially)"
                return caption
            },
            focusCoverageHint() {
                if (!this.coverageStats.focusTotal) return ""
                return "AI judgement of the page content, not measured visibility — what AI assistants actually cite is listed per topic below."
            },
            // three different numbers, three different words: covered (X) of relevant (Y),
            // relevant of sampled candidates (Z), sampled of tracked (N)
            promptCoverageCaption() {
                const stats = this.coverageStats
                if (!stats.promptsTotal) {
                    if (!this.promptsNotRelevant) return "Not analyzed for this audit."
                    return "None of the " + stats.promptsCandidates + " sampled prompts match this content."
                }
                // results from before the relevance filter judged every sampled prompt — calling
                // those "relevant" would claim a filtering step that never ran
                const relevant = stats.promptsCandidates !== null ? " relevant" : ""
                let caption = stats.promptsCovered + " of " + stats.promptsTotal + relevant + " prompt" + (stats.promptsTotal === 1 ? "" : "s") + " answered on " + this.pageWord
                if (stats.promptsPartial) caption += " (" + stats.promptsPartial + " partially)"
                return caption
            },
            promptCoverageHint() {
                const stats = this.coverageStats
                const sampledFrom = stats.promptsTracked !== null && stats.promptsCandidates !== null && stats.promptsTracked > stats.promptsCandidates
                if (!stats.promptsTotal) {
                    // the caption already carries "none of the N sampled" — only the tracked total is left
                    if (!this.promptsNotRelevant || !sampledFrom) return ""
                    return "Sampled from " + stats.promptsTracked + " tracked prompts."
                }
                // both numbers describe the same run-level set (every URL is judged against the same
                // candidates, the analyzed prompts are merged across URLs) — if a stored document ever
                // breaks that, the comparison is dropped instead of printing a nonsense ratio
                if (stats.promptsCandidates !== null && stats.promptsCandidates >= stats.promptsTotal) {
                    let hint = stats.promptsTotal + " of " + stats.promptsCandidates + " sampled prompts are relevant for " + this.pageWord
                    hint += sampledFrom ? " (sampled from " + stats.promptsTracked + " tracked)." : "."
                    return hint
                }
                // older results carry no candidate count, only the tracked total
                if (stats.promptsTracked === null || stats.promptsTracked <= stats.promptsTotal) return ""
                return "Sampled from " + stats.promptsTracked + " tracked prompts."
            },
            fanoutCoverageCaption() {
                const stats = this.coverageStats
                if (!stats.fanoutTotal) {
                    if (!this.fanoutNotRelevant) return "Not analyzed for this audit."
                    return "None of the " + stats.fanoutCandidates + " sampled fan-out queries match this content."
                }
                const relevant = stats.fanoutCandidates !== null ? " relevant" : ""
                let caption = stats.fanoutCovered + " of " + stats.fanoutTotal + relevant + " sub-question" + (stats.fanoutTotal === 1 ? "" : "s") + " answered"
                if (stats.fanoutPartial) caption += " (" + stats.fanoutPartial + " partially)"
                return caption
            },
            fanoutCoverageHint() {
                const stats = this.coverageStats
                // the caption already carries the candidate count in the not-relevant case
                if (!stats.fanoutTotal) return ""
                if (stats.fanoutCandidates !== null && stats.fanoutCandidates >= stats.fanoutTotal) {
                    return stats.fanoutTotal + " of " + stats.fanoutCandidates + " sampled fan-out queries are relevant for " + this.pageWord + "."
                }
                return "Pages covering fan-out sub-questions are cited far more often."
            },
            // prompts and fan-out queries merged across all URLs, deduplicated by text, best verdict wins
            mergedCoverage() {
                const collect = (type, listKey, textKeys) => {
                    const seen = new Map()
                    const items = []
                    this.coverageResults.forEach((res) => {
                        const list = res.coverage && Array.isArray(res.coverage[listKey]) ? res.coverage[listKey] : []
                        list.forEach((entry) => {
                            if (!entry || typeof entry !== "object") return
                            const text = textKeys.map((key) => (typeof entry[key] === "string" ? entry[key].trim() : "")).find((value) => value) || ""
                            const verdict = this.coverageVerdict(entry.verdict)
                            if (!text || !verdict) return
                            const gap = typeof entry.gap === "string" ? entry.gap.trim() : ""
                            const uses = this.toNumber(entry.uses) || 0
                            const key = text.toLowerCase()
                            const existing = seen.get(key)
                            if (!existing) {
                                const item = { key: type + "|" + key, type: type, text: text, verdict: verdict, gap: gap, uses: uses, order: items.length }
                                seen.set(key, item)
                                items.push(item)
                                return
                            }
                            if (uses > existing.uses) existing.uses = uses
                            if (VERDICT_RANK[verdict] > VERDICT_RANK[existing.verdict]) {
                                existing.verdict = verdict
                                existing.gap = gap
                            } else if (!existing.gap && gap) {
                                existing.gap = gap
                            }
                        })
                    })
                    return items
                }
                // focus is optional and missing on results audited before focus entries existed;
                // its text field follows the prompt path, so both spellings are accepted
                return {
                    focus: collect("focus", "focus", ["query", "prompt"]),
                    prompts: collect("prompt", "prompts", ["prompt"]),
                    fanout: collect("fanout", "fanout", ["query"])
                }
            },
            // everything not fully covered: focus first (the user asked for those explicitly),
            // then missing before partial, prompts in original order, fan-out by usage
            coverageGaps() {
                const focus = this.mergedCoverage.focus.filter((item) => item.verdict !== "covered").sort((a, b) => a.order - b.order)
                const prompts = this.mergedCoverage.prompts.filter((item) => item.verdict !== "covered").sort((a, b) => a.order - b.order)
                const fanout = this.mergedCoverage.fanout.filter((item) => item.verdict !== "covered").sort((a, b) => b.uses - a.uses || a.order - b.order)
                const byVerdict = (list, verdict) => list.filter((item) => item.verdict === verdict)
                return byVerdict(focus, "missing")
                    .concat(byVerdict(focus, "partial"))
                    .concat(byVerdict(prompts, "missing"), byVerdict(fanout, "missing"))
                    .concat(byVerdict(prompts, "partial"), byVerdict(fanout, "partial"))
            },
            visibleCoverageGaps() {
                return this.coverageGapsOpen ? this.coverageGaps : this.coverageGaps.slice(0, this.gapLimit)
            },
            // coverage score per result id for the table column (summary rows do not carry coverage).
            // The halves are scored independently, so a focus-only run has no prompt score at all —
            // the column takes the best available half: focus first (the user asked for those),
            // then the project prompts, then the fan-out queries.
            coverageByResultId() {
                const map = {}
                this.results.forEach((res) => {
                    const coverage = res && res.coverage && typeof res.coverage === "object" ? res.coverage : null
                    if (!coverage) return
                    const halfScore = (totalKey, scoreKey) => {
                        if (!this.toNumber(coverage[totalKey])) return null
                        return this.toNumber(coverage[scoreKey])
                    }
                    const score = [halfScore("focus_total", "focus_score"), halfScore("prompts_total", "prompt_score"), halfScore("fanout_total", "fanout_score")].find(
                        (value) => value !== null
                    )
                    if (score === null || score === undefined) return
                    map[String(res._id)] = Math.round(score)
                })
                return map
            },
            // ---- content stats / passage analysis of the opened result ----
            detailContentStats() {
                const stats = this.detail && this.detail.content_stats
                return stats && typeof stats === "object" ? stats : null
            },
            detailFactsPer100() {
                if (!this.detailContentStats) return null
                return this.toNumber(this.detailContentStats.facts_per_100_words)
            },
            detailSections() {
                return this.detail && Array.isArray(this.detail.sections) ? this.detail.sections.filter((section) => section && typeof section === "object") : []
            },
            // sections the LLM actually judged (old results and failed calls carry null fields)
            analyzedSections() {
                return this.detailSections.filter((section) => typeof section.answer_first === "boolean" || typeof section.self_contained === "boolean" || typeof section.topic_drift === "boolean")
            },
            problemSections() {
                return this.analyzedSections
                    .filter((section) => section.answer_first === false || section.self_contained === false || section.topic_drift === true)
                    .slice()
                    .sort((a, b) => (this.toNumber(a.index) || 0) - (this.toNumber(b.index) || 0))
            },
            wellStructuredCount() {
                return Math.max(0, this.analyzedSections.length - this.problemSections.length)
            },
            urlHeaders() {
                const headers = [
                    { text: "URL", value: "url", sortable: true },
                    { text: "Score", value: "total", sortable: false },
                    { text: "Issues", value: "issues", sortable: false },
                    { text: "Technical", value: "technical", sortable: false },
                    { text: "Structure", value: "structure", sortable: false },
                    { text: "Machine", value: "machine", sortable: false },
                    { text: "Citability", value: "citability", sortable: false },
                    { text: "JavaScript", value: "js_verdict", sortable: true }
                ]
                if (this.run && this.run.project_id) {
                    headers.push({ text: "Cited", value: "cited_count", sortable: true })
                }
                if (this.hasCoverageColumn) {
                    headers.push({ text: "Coverage", value: "coverage", sortable: false })
                }
                return headers
            },
            detailJsVerdict() {
                return this.detail && this.detail.js_dependency ? this.detail.js_dependency.verdict : null
            },
            detailCitedCount() {
                return this.detail && this.detail.tracking && typeof this.detail.tracking.cited_count === "number" ? this.detail.tracking.cited_count : 0
            },
            hasLlm() {
                const llm = this.detail && this.detail.llm
                return !!(llm && ((llm.quotable_passages && llm.quotable_passages.length) || (llm.recommendations && llm.recommendations.length) || llm.citability !== null))
            },
            // ---- ready-to-use fixes of the opened result (generated on demand, persisted server-side) ----
            // fixes are optional everywhere: old results and results that were never generated carry none
            // hidden once every generated block sits on its recommendation — an empty card at the end of
            // the page is exactly the detached leftover this layout is meant to get rid of. A generation
            // that produced nothing at all keeps the card: its empty state is the only place that says so.
            showFixesCard() {
                return !!(this.detail && this.detail.status === "done") && !this.fixesAllMaterialBound
            },
            // any generation in flight (blocks starting a second one, whichever URL is open)
            fixesGenerating() {
                return this.fixesLoadingId !== null
            },
            // generation of the URL that is currently shown — drives spinner and button label
            fixesLoading() {
                return !!(this.fixesLoadingId !== null && this.detail && this.detail._id && String(this.detail._id) === String(this.fixesLoadingId))
            },
            detailFixes() {
                const fixes = this.detail ? this.detail.fixes : null
                return fixes && typeof fixes === "object" && !Array.isArray(fixes) ? fixes : null
            },
            fixesGeneratedAt() {
                const stamp = this.detail ? this.detail.fixes_generated_at : null
                return stamp || null
            },
            // a run of the generator that produced nothing is still "generated" — the card must not
            // fall back to its initial state and invite the user to generate again forever
            fixesGenerated() {
                return !!(this.detailFixes || this.fixesGeneratedAt)
            },
            fixesTitle() {
                return this.fixText(this.detailFixes && this.detailFixes.title)
            },
            fixesMetaDescription() {
                return this.fixText(this.detailFixes && this.detailFixes.meta_description)
            },
            fixesIntro() {
                return this.fixText(this.detailFixes && this.detailFixes.intro)
            },
            // single-line-ish blocks share one renderer: label, optional character count, copy button
            fixTextBlocks() {
                const blocks = []
                if (this.fixesTitle) blocks.push({ key: "title", label: "Title", text: this.fixesTitle, range: FIX_TITLE_RANGE })
                if (this.fixesMetaDescription) blocks.push({ key: "meta", label: "Meta description", text: this.fixesMetaDescription, range: FIX_META_RANGE })
                if (this.fixesIntro) blocks.push({ key: "intro", label: "Intro (answer-first)", text: this.fixesIntro, range: null })
                return blocks
            },
            fixesFaq() {
                const list = this.detailFixes && Array.isArray(this.detailFixes.faq) ? this.detailFixes.faq : []
                return list
                    .map((entry, i) => {
                        if (!entry || typeof entry !== "object") return null
                        const status = typeof entry.status === "string" ? entry.status.trim().toLowerCase() : ""
                        return {
                            key: "fixfaq-" + i,
                            question: this.fixText(entry.question),
                            answer: this.fixText(entry.answer),
                            outline: status === "outline"
                        }
                    })
                    .filter((entry) => entry && (entry.question || entry.answer))
            },
            // pretty-printed when the generator returned parsable JSON, raw string otherwise
            fixesSchema() {
                const raw = this.fixText(this.detailFixes && this.detailFixes.schema_jsonld)
                if (!raw) return ""
                try {
                    return JSON.stringify(JSON.parse(raw), null, 2)
                } catch (error) {
                    return raw
                }
            },
            fixesFacts() {
                const list = this.detailFixes && Array.isArray(this.detailFixes.facts) ? this.detailFixes.facts : []
                return list.map((fact) => this.fixText(fact)).filter(Boolean)
            },
            // ready-to-paste material the generator derived from the AI content recommendations.
            // Optional everywhere: results generated before blocks existed simply carry none.
            fixesBlocks() {
                const list = this.detailFixes && Array.isArray(this.detailFixes.blocks) ? this.detailFixes.blocks : []
                return list
                    .map((entry, i) => {
                        if (!entry || typeof entry !== "object" || Array.isArray(entry)) return null
                        const kind = this.fixText(entry.kind).toLowerCase()
                        // own-property lookup on purpose: "constructor" and friends are not kinds
                        const known = Object.prototype.hasOwnProperty.call(FIX_BLOCK_KIND_LABELS, kind)
                        const normalized = known ? kind : FIX_BLOCK_KIND_FALLBACK
                        return {
                            key: "fixblock-" + i,
                            title: this.fixText(entry.title),
                            kind: normalized,
                            kindLabel: FIX_BLOCK_KIND_LABELS[normalized],
                            content: this.fixText(entry.content),
                            note: this.fixText(entry.note),
                            recommendation: this.fixText(entry.recommendation),
                            // markup and JSON-LD are rendered verbatim: their brackets and attributes
                            // are not placeholders, so the highlighter must not touch them
                            preformatted: normalized === "html" || normalized === "jsonld",
                            // HTML is often one very long line — wrap it instead of forcing a horizontal
                            // scroll. JSON-LD carries meaningful indentation and keeps it (scrolls instead).
                            wraps: normalized === "html"
                        }
                    })
                    .filter((entry) => entry && entry.title && entry.content)
            },
            // how many blocks the generator actually filled — the whole material of the open result,
            // bound and unbound alike (the brief copies exactly this set)
            // compact list for the overview card: label, one-line preview and the text to copy per
            // generated block — the full blocks with placeholder highlighting live in the Fixes tab
            overviewMaterial() {
                const preview = (text) => {
                    const flat = String(text || "")
                        .replace(/\s+/g, " ")
                        .trim()
                    return flat.length > OVERVIEW_PREVIEW_CHARS ? flat.slice(0, OVERVIEW_PREVIEW_CHARS - 1) + "…" : flat
                }
                const plural = (count, word) => count + " " + word + (count === 1 ? "" : "s")
                const items = []
                this.fixTextBlocks.forEach((block) => items.push({ key: "ov-" + block.key, label: block.label, preview: preview(block.text), text: block.text }))
                if (this.fixesFaq.length) {
                    items.push({ key: "ov-faq", label: "FAQ", preview: plural(this.fixesFaq.length, "question") + " · " + preview(this.fixesFaq[0].question), text: this.faqPlainText })
                }
                if (this.fixesSchema) items.push({ key: "ov-schema", label: "Schema.org JSON-LD", preview: preview(this.fixesSchema), text: this.fixesSchema })
                if (this.fixesFacts.length) {
                    items.push({ key: "ov-facts", label: "Facts to add", preview: plural(this.fixesFacts.length, "fact") + " · " + preview(this.fixesFacts[0]), text: this.fixesFacts.join("\n") })
                }
                this.fixesBlocks.forEach((block) => items.push({ key: "ov-" + block.key, label: block.title, preview: preview(block.content), text: block.content }))
                return items.slice(0, OVERVIEW_MATERIAL_LIMIT)
            },
            fixBlockCount() {
                let count = this.fixTextBlocks.length
                if (this.fixesFaq.length) count++
                if (this.fixesSchema) count++
                if (this.fixesFacts.length) count++
                return count + this.fixesBlocks.length
            },
            // ---- material bound to the recommendation it realizes ----
            // The recommendation list is aggregated over every URL of the run, the generated blocks belong
            // to the opened result: a block is only ever offered on a row of that same result. Each block
            // is claimed by at most one row, everything left over stays in the card below the list.
            recommendationBinding() {
                const detailId = this.detail && this.detail._id ? String(this.detail._id) : ""
                const rows = this.llmActionRecommendations.map((reco, i) => ({
                    // index based: the sorted list only changes when the loaded results change
                    key: "llmreco" + i,
                    priority: reco.priority,
                    text: reco.text,
                    url: reco.url,
                    normalized: this.normalizedRecoText(reco.text),
                    belongsToDetail: !!detailId && String(reco.result_id) === detailId,
                    blocks: []
                }))
                const bound = {}
                if (detailId) {
                    const candidates = rows.filter((entry) => entry.belongsToDetail && entry.normalized)
                    this.fixesBlocks.forEach((block) => {
                        const target = this.normalizedRecoText(block.recommendation)
                        if (!target) return
                        // exact match first, and across ALL rows: one recommendation can be the literal
                        // beginning of another ("… top 5 products" / "… top 5 products and their prices"),
                        // and the shorter one must not swallow the longer one's material
                        let row = candidates.find((entry) => entry.normalized === target)
                        if (!row) {
                            // no exact text anywhere: the shortened/extended variant only identifies a row
                            // when exactly one row matches — an ambiguous prefix stays unbound
                            const matches = candidates.filter((entry) => this.recoTextMatches(entry.normalized, target))
                            if (matches.length !== 1) return
                            row = matches[0]
                        }
                        row.blocks.push(block)
                        bound[block.key] = true
                    })
                }
                return { rows: rows, bound: bound }
            },
            recommendationRows() {
                return this.recommendationBinding.rows
            },
            // blocks that matched no recommendation of the open result — those keep their place in the card
            unboundFixesBlocks() {
                const bound = this.recommendationBinding.bound
                return this.fixesBlocks.filter((block) => !bound[block.key])
            },
            // what the card itself still shows — the pill and its empty state must not count material
            // that was moved up into the recommendation list
            fixesCardBlockCount() {
                let count = this.fixTextBlocks.length
                if (this.fixesFaq.length) count++
                if (this.fixesSchema) count++
                if (this.fixesFacts.length) count++
                return count + this.unboundFixesBlocks.length
            },
            // every generated block ended up on a recommendation: the card would only be a headline
            fixesAllMaterialBound() {
                return !!(this.fixesGenerated && this.fixBlockCount > 0 && this.fixesCardBlockCount === 0)
            },
            recommendationsWithMaterial() {
                return this.recommendationRows.filter((row) => row.blocks.length)
            },
            // exactly one recommendation carries material: nothing to choose between, so open it
            defaultOpenRecoKey() {
                const list = this.recommendationsWithMaterial
                return list.length === 1 ? list[0].key : ""
            },
            // the generation button in the recommendations header acts on the opened result
            canGenerateMaterial() {
                return !!(this.detail && this.detail._id && this.detail.status === "done")
            },
            materialButtonDisabled() {
                return !this.canGenerateMaterial || this.fixesGenerating || !this.$store.getters.canWriteAiSuite
            },
            // one call fills every row, so the wait is explained once, under the header
            materialHeaderCaption() {
                if (this.fixesLoading) return "Generating ready-to-paste material for all recommendations …"
                // a generation of ANOTHER URL blocks this button too — without this branch the button
                // is disabled, shows no spinner, and the list below still asks to press it
                if (this.fixesGenerating) return "A generation is running for another URL — the button is available again once it finishes."
                if (this.isMultiUrl && !this.detail) return "Select a URL above to generate material."
                return ""
            },
            // no per-row button: the header button generates everything at once. Silent while ANY
            // generation runs — the header caption owns that state.
            materialListCaption() {
                if (this.fixesGenerating || this.fixesGenerated || !this.canGenerateMaterial) return ""
                if (!this.recommendationRows.some((row) => row.belongsToDetail)) return ""
                return "Generate material to turn these recommendations into copy-paste blocks."
            },
            // the material of the opened URL, named wherever the per-URL card and the per-URL blocks
            // can drift away from the table that says which URL is open (stacked multi-URL layout)
            materialScopeUrl() {
                return this.isMultiUrl && this.detail ? this.shortUrl(this.detail.url) : ""
            },
            fixesCardEyebrow() {
                return this.materialScopeUrl ? "More ready-to-paste material · " + this.materialScopeUrl : "More ready-to-paste material"
            },
            // the fix brief and the "always review" disclaimer live on the card — but the card is gone
            // once every generated block moved up onto its recommendation. Exactly one of the two
            // places renders them: the header takes over whenever the card is hidden.
            showHeaderFixBrief() {
                return !!(this.fixesGenerated && this.fixBlockCount && !this.showFixesCard)
            },
            // content recommendations of the opened page — the empty state has to distinguish
            // "there was nothing to do" from "there was advice but no material came out of it"
            detailRecommendations() {
                const llm = this.detail && this.detail.llm
                const list = llm && Array.isArray(llm.recommendations) ? llm.recommendations : []
                return list.filter((reco) => reco && this.fixText(reco.text))
            },
            // failed and warned checks of the opened page — only counted once the full results are
            // loaded, so an unloaded run simply points at nothing instead of claiming "no issues"
            detailOpenIssues() {
                const info = this.detail ? this.urlIssues(this.detail) : null
                return info && info.total ? info.total : 0
            },
            // the card only knows that nothing could be derived — it knows nothing about the state
            // of the checks, so it must not claim they pass. Open checks are named as the place to
            // look instead, they live in the action list of this run.
            fixesEmptyText() {
                if (this.detailRecommendations.length) return "No ready-to-paste material could be derived — see the AI content recommendations above."
                if (this.detailOpenIssues) return 'No ready-to-paste material could be derived for this page — the open checks are listed under "Tackle this first".'
                return "No ready-to-paste material could be derived for this page."
            },
            // the material blocks are newer than the first generated fixes: results generated before
            // them carry none and are returned from the cache unchanged, so nothing on the card would
            // otherwise tell the user that regenerating now produces material for the recommendations
            regenerateHint() {
                if (!this.fixesGenerated || this.fixesBlocks.length || !this.detailRecommendations.length) return ""
                return "Regenerate to get ready-to-paste material for the recommendations."
            },
            faqPlainText() {
                const lines = []
                this.fixesFaq.forEach((entry) => {
                    // the outline marker travels with the copied text: an outline is not an answer
                    if (entry.question) lines.push(entry.question + (entry.outline ? OUTLINE_MARKER : ""))
                    else if (entry.outline) lines.push(OUTLINE_MARKER.trim())
                    if (entry.answer) lines.push(entry.answer)
                    lines.push("")
                })
                if (!lines.length) return ""
                lines.push(REVIEW_WARNING)
                return lines.join("\n").trim()
            },
            // one plain-text brief with every generated block, ready to paste into a ticket
            fixBriefText() {
                if (!this.fixBlockCount) return ""
                const lines = ["Fix brief for " + ((this.detail && this.detail.url) || "this page")]
                const section = (label, value) => {
                    if (!value) return
                    lines.push("", label, value)
                }
                section("TITLE", this.fixesTitle)
                section("META DESCRIPTION", this.fixesMetaDescription)
                section("INTRO", this.fixesIntro)
                if (this.fixesFaq.length) {
                    lines.push("", "FAQ")
                    this.fixesFaq.forEach((entry) => {
                        // the outline marker travels with the copied text: an outline is not an answer
                        lines.push("- " + (entry.question || "") + (entry.outline ? OUTLINE_MARKER : ""))
                        if (entry.answer) lines.push("  " + entry.answer)
                    })
                }
                section("SCHEMA (JSON-LD)", this.fixesSchema)
                if (this.fixesFacts.length) {
                    lines.push("", "FACTS TO ADD")
                    this.fixesFacts.forEach((fact) => lines.push("- " + fact))
                }
                if (this.fixesBlocks.length) {
                    lines.push("", "BLOCKS")
                    this.fixesBlocks.forEach((block) => {
                        lines.push("", block.title)
                        if (block.note) lines.push(block.note)
                        lines.push(block.content)
                    })
                }
                lines.push("", REVIEW_WARNING)
                return lines.join("\n")
            }
        },
        methods: {
            statusColor(status) {
                const map = { pending: "grey", processing: "info", done: "primary", failed: "danger" }
                return map[status] || "grey"
            },
            statusLabel(status) {
                const map = { pending: "Waiting", processing: "Running", done: "Done", failed: "Failed" }
                return map[status] || status
            },
            scoreColor(score) {
                if (score >= 80) return "success"
                if (score >= 60) return "warning"
                return "error"
            },
            scoreColorHex(score) {
                if (score === null || score === undefined) return "#3d6d66"
                if (score >= 80) return "#007a68"
                if (score >= 60) return "#9a6700"
                return "#b32d2d"
            },
            jsChip(verdict) {
                const map = {
                    ok: { color: "primary", label: "JS ok" },
                    partial: { color: "warn", label: "JS partial" },
                    critical: { color: "danger", label: "JS blocked" }
                }
                return map[verdict] || { color: "grey", label: "JS unknown" }
            },
            priorityColor(priority) {
                const map = { high: "danger", medium: "warn", low: "grey" }
                return map[priority] || "grey"
            },
            priorityLabel(priority) {
                const map = { high: "High", medium: "Medium", low: "Low" }
                return map[priority] || priority
            },
            priorityRank(priority) {
                return PRIORITY_RANK[priority] !== undefined ? PRIORITY_RANK[priority] : 3
            },
            checkImpact(check) {
                return typeof check.impact === "number" ? check.impact : IMPACT_FALLBACK
            },
            categoryLabel(key) {
                const meta = CATEGORY_META.find((c) => c.key === key)
                return meta ? meta.label : key
            },
            actionRecommendation(action) {
                const first = action.affected[0]
                if (!first) return ""
                return first.recommendation || first.details || ""
            },
            // all checks of every done result that carry the given status
            checksWithStatus(status) {
                const items = []
                this.doneResults.forEach((res) => {
                    ;(res.checks || []).forEach((check) => {
                        if (check && check.status === status) items.push(check)
                    })
                })
                return items
            },
            urlIssues(item) {
                if (!item || !item._id) return null
                return this.issuesByResultId[String(item._id)] || null
            },
            // per-URL category score for the table columns (summary rows carry the whole scores object)
            categoryScore(item, key) {
                const score = item && item.scores ? this.toNumber(item.scores[key]) : null
                return score === null ? null : Math.round(score)
            },
            issueColor(info) {
                if (!info) return "grey"
                if (info.fail) return "error"
                if (info.warn) return "warning"
                return "success"
            },
            // returns a finite number or null — keeps the new GEO fields from ever rendering NaN
            toNumber(value) {
                const num = typeof value === "number" ? value : parseFloat(value)
                return typeof num === "number" && isFinite(num) ? num : null
            },
            coverageVerdict(verdict) {
                const value = typeof verdict === "string" ? verdict.trim().toLowerCase() : ""
                return VERDICT_RANK[value] !== undefined ? value : null
            },
            coverageScore(item) {
                if (!item || !item._id) return null
                const score = this.coverageByResultId[String(item._id)]
                return score === undefined ? null : score
            },
            // content-gap chips: the source of the entry (focus keeps its own colour so the
            // entries the user asked for stay recognisable), verdict colour for the rest
            gapTypeLabel(type) {
                if (type === "focus") return "Focus"
                if (type === "fanout") return "Fan-out"
                return "Prompt"
            },
            gapTypeClass(gap) {
                if (gap && gap.type === "focus") return "q-pill--info"
                return gap && gap.verdict === "missing" ? "q-pill--danger" : "q-pill--warn"
            },
            gapVerdictLabel(gap) {
                return gap && gap.verdict === "missing" ? "Missing" : "Partial"
            },
            gapVerdictClass(gap) {
                return gap && gap.verdict === "missing" ? "seoaudit-run__chip--danger" : "seoaudit-run__chip--warn"
            },
            factsChipClass(value) {
                if (value >= 2.5) return "seoaudit-run__chip--success"
                if (value >= 1) return "seoaudit-run__chip--warn"
                return "seoaudit-run__chip--danger"
            },
            // "Intro" only for the section the backend flagged as intro (older results have no
            // is_intro flag — there the text before the first heading is index 0)
            sectionHeading(section) {
                const heading = section && typeof section.heading === "string" ? section.heading.trim() : ""
                if (heading) return heading
                const index = this.toNumber(section && section.index)
                if (section && section.is_intro === true) return "Intro"
                if (section && section.is_intro === undefined && index === 0) return "Intro"
                return "Section " + ((index === null ? 0 : index) + 1)
            },
            sectionProblems(section) {
                const tags = []
                if (!section) return tags
                if (section.answer_first === false) tags.push({ label: "Not answer-first", cls: "seoaudit-run__chip--danger" })
                if (section.self_contained === false) tags.push({ label: "Needs context", cls: "seoaudit-run__chip--warn" })
                if (section.topic_drift === true) tags.push({ label: "Topic drift", cls: "seoaudit-run__chip--warn" })
                return tags
            },
            // plain-text outline of every open gap, ready to paste into a content brief
            copyContentOutline() {
                const gaps = this.coverageGaps
                if (!gaps.length) return
                const name = (this.run && this.run.name) || "this audit"
                const lines = ["Content gaps for " + name + ":", ""]
                gaps.forEach((gap) => {
                    lines.push("- " + gap.text)
                    if (gap.gap) lines.push("  Missing: " + gap.gap)
                })
                this.copyToClipboard(lines.join("\n"), "Content outline copied")
            },
            // clipboard API with a legacy execCommand fallback (older browsers / insecure contexts)
            copyToClipboard(text, message) {
                const value = typeof text === "string" ? text : ""
                if (!value) return
                const confirmCopy = () => {
                    this.notification = this.$helpers.createNotification(message, "mdi-check", "success")
                }
                const failCopy = () => {
                    this.notification = this.$helpers.createNotification("Could not copy to the clipboard.", "mdi-alert-circle-outline", "error")
                }
                const fallback = () => {
                    const area = document.createElement("textarea")
                    let copied = false
                    try {
                        area.value = value
                        area.setAttribute("readonly", "")
                        area.style.position = "fixed"
                        area.style.top = "-1000px"
                        area.style.opacity = "0"
                        document.body.appendChild(area)
                        area.select()
                        copied = document.execCommand("copy")
                    } catch (error) {
                        console.error("copyToClipboard error", error)
                        copied = false
                    } finally {
                        // the textarea must never survive a throwing execCommand
                        if (area.parentNode) area.parentNode.removeChild(area)
                    }
                    if (copied) confirmCopy()
                    else failCopy()
                }
                if (navigator && navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
                    navigator.clipboard.writeText(value).then(confirmCopy).catch(fallback)
                    return
                }
                fallback()
            },
            // ---- ready-to-use fixes ----
            // the generated shape is never trusted: anything but a non-empty string becomes ""
            fixText(value) {
                return typeof value === "string" ? value.trim() : ""
            },
            // splits generated text into plain parts and "[...]" placeholders so the editor sees
            // at a glance what still has to be filled in before publishing
            placeholderParts(text) {
                const value = this.fixText(text)
                if (!value) return []
                return value
                    .split(PLACEHOLDER_SPLIT)
                    .filter((part) => part !== "" && part !== undefined)
                    .map((part, i) => ({ key: "part-" + i, text: part, placeholder: isPlaceholderPart(part) }))
            },
            partClass(part) {
                return part && part.placeholder ? "seoaudit-run__placeholder" : ""
            },
            // headline label, not a link: a long URL must not push the head into three lines.
            // The scheme carries nothing here, the beginning identifies the page.
            shortUrl(value, max = 52) {
                const text = this.fixText(value).replace(/^https?:\/\//i, "")
                return text.length > max ? text.slice(0, max - 1) + "…" : text
            },
            // recommendation texts are compared case- and whitespace-insensitively: the backend stores the
            // canonical text on the block, but a regenerated block may differ in wrapping alone.
            // The leading markers are stripped exactly like stripRecommendationMarker() does on the
            // backend — otherwise a recommendation that carries one could never match its own block.
            normalizedRecoText(value) {
                return this.fixText(value)
                    .replace(/^\[\d+\]\s*/, "")
                    .replace(/^\((?:high|medium|low)\)\s*/i, "")
                    .trim()
                    .toLowerCase()
                    .replace(/\s+/g, " ")
            },
            // exact match first; a shortened or extended variant only counts when the shared beginning is
            // long enough to identify the recommendation ("Add a FAQ" would match far too much)
            recoTextMatches(a, b) {
                if (!a || !b) return false
                if (a === b) return true
                const shorter = a.length <= b.length ? a : b
                const longer = a.length <= b.length ? b : a
                return shorter.length >= 20 && longer.indexOf(shorter) === 0
            },
            isRecoMaterialOpen(row) {
                if (!row) return false
                const state = this.expandedRecoMaterial[row.key]
                if (typeof state === "boolean") return state
                return row.key === this.defaultOpenRecoKey
            },
            toggleRecoMaterial(row) {
                if (!row) return
                this.$set(this.expandedRecoMaterial, row.key, !this.isRecoMaterialOpen(row))
            },
            charCountClass(block) {
                if (!block || !block.range) return ""
                const length = block.text.length
                return length < block.range.min || length > block.range.max ? "seoaudit-run__charcount--warn" : ""
            },
            // writes the generated fixes onto the opened result and its entry in the loaded results,
            // so switching between URLs keeps them without another request
            applyFixes(resultId, fixes, generatedAt) {
                const value = fixes && typeof fixes === "object" && !Array.isArray(fixes) ? fixes : {}
                const stamp = generatedAt || new Date().toISOString()
                const targets = []
                if (this.detail && String(this.detail._id) === String(resultId)) targets.push(this.detail)
                this.results.forEach((res) => {
                    if (res && String(res._id) === String(resultId) && targets.indexOf(res) === -1) targets.push(res)
                })
                targets.forEach((target) => {
                    this.$set(target, "fixes", value)
                    this.$set(target, "fixes_generated_at", stamp)
                })
                return targets.length
            },
            // known backend markers get readable text, everything else the generic message —
            // an unmarked 502/504 (proxy, gateway) says nothing about the audited page
            fixErrorMessage(error) {
                const status = error?.response?.status
                const marker = error?.response?.data?.error
                const known = FIX_ERROR_MESSAGES.find((entry) => entry.status === status && entry.error === marker)
                return known ? known.message : FIX_ERROR_FALLBACK
            },
            async generateFixSuggestions(regenerate = false) {
                if (!this.detail || !this.detail._id || this.fixesGenerating) return
                const resultId = String(this.detail._id)
                // captured now: a run switch bumps the token and makes this response stale
                const token = ++this.fixesRequestToken
                try {
                    this.fixesLoadingId = resultId
                    const data = await generateFixes(localStorage.company_id, {
                        runId: this.$route.params.id,
                        resultId: resultId,
                        regenerate: !!regenerate
                    })
                    if (this.isDestroyed || token !== this.fixesRequestToken) return
                    if (data && data.error) {
                        this.notification = this.$helpers.createNotification(data.error, "mdi-alert-circle-outline", "error")
                        return
                    }
                    // a 200 without a usable fixes object must not flip the card into "nothing needed"
                    const fixes = data && data.fixes && typeof data.fixes === "object" && !Array.isArray(data.fixes) ? data.fixes : null
                    if (!fixes) {
                        this.notification = this.$helpers.createNotification(FIX_ERROR_FALLBACK, "mdi-alert-circle-outline", "error")
                        return
                    }
                    const stamp = data.fixes_generated_at || fixes.generated_at || null
                    this.applyFixes(resultId, fixes, stamp)
                    this.notification = this.$helpers.createNotification("Fix suggestions ready.", "mdi-check-circle", "primary")
                } catch (error) {
                    if (this.isDestroyed || token !== this.fixesRequestToken) return
                    console.error("generateFixSuggestions error", error)
                    this.notification = this.$helpers.createNotification(this.fixErrorMessage(error), "mdi-alert-circle-outline", "error")
                } finally {
                    // a stale response must not clear the flag of the generation that replaced it
                    if (!this.isDestroyed && token === this.fixesRequestToken) this.fixesLoadingId = null
                }
            },
            copyAllFixes() {
                this.copyToClipboard(this.fixBriefText, "Fix brief copied")
            },
            copyAllRecommendations() {
                this.copyToClipboard(this.recommendationsBriefText, "Recommendations copied")
            },
            toggleAction(id) {
                this.$set(this.expandedActions, id, !this.expandedActions[id])
            },
            toggleCategoryFilter(key) {
                this.activeCategory = this.activeCategory === key ? null : key
            },
            // overview tile: filter the fixes by that category and go where they are rendered
            openCategory(tile) {
                if (!tile.failCount && !tile.warnCount) return
                this.activeCategory = tile.key
                this.activeTab = "fixes"
            },
            // overview row: open that fix expanded on the fixes tab
            openFix(action) {
                this.activeCategory = null
                this.activeTab = "fixes"
                this.$set(this.expandedActions, action.id, true)
            },
            formatDate(date) {
                if (!date) return "-"
                const d = new Date(date)
                if (isNaN(d)) return "-"
                return d.toISOString().slice(0, 10)
            },
            async loadRun(silent = false) {
                try {
                    if (!silent) this.loading = true
                    const data = await fetchRun(localStorage.company_id, this.$route.params.id)
                    if (this.isDestroyed) return
                    this.run = data.run || null
                    this.summary = Array.isArray(data.results_summary) ? data.results_summary : []
                    if (this.isActive) {
                        this.startPolling()
                    } else {
                        this.stopPolling()
                    }
                    if (this.run && this.run.status === "done") this.loadAllResults()
                } catch (error) {
                    if (this.isDestroyed) return
                    console.error("loadRun error", error)
                    if (error?.response?.status === 404) {
                        this.stopPolling()
                        this.run = null
                        this.summary = []
                        return
                    }
                    if (!silent) {
                        this.stopPolling()
                        const message = error?.response?.data?.error || "Error loading the audit. Please try again later."
                        this.notification = this.$helpers.createNotification(message, "mdi-alert-circle-outline", "error")
                    }
                } finally {
                    if (!silent) this.loading = false
                }
            },
            async loadAllResults() {
                if (this.resultsLoaded || this.resultsLoading) return
                try {
                    this.resultsLoading = true
                    const results = await fetchResults(localStorage.company_id, this.$route.params.id)
                    if (this.isDestroyed) return
                    this.results = Array.isArray(results) ? results : []
                    this.resultsLoaded = true
                    // 1-URL runs: auto-open the detail for the single result (drives the side cards)
                    if (this.results.length === 1) this.detail = this.results[0]
                } catch (error) {
                    if (this.isDestroyed) return
                    console.error("loadAllResults error", error)
                    const message = error?.response?.data?.error || "Error loading the audit results."
                    this.notification = this.$helpers.createNotification(message, "mdi-alert-circle-outline", "error")
                } finally {
                    if (!this.isDestroyed) this.resultsLoading = false
                }
            },
            startPolling() {
                if (this.isDestroyed || this.pollInterval) return
                this.pollInterval = setInterval(() => {
                    this.loadRun(true)
                }, 5000)
            },
            stopPolling() {
                if (this.pollInterval) {
                    clearInterval(this.pollInterval)
                    this.pollInterval = null
                }
            },
            // the coverage of the opened page was re-checked with a new prompt selection: swap the
            // new coverage into the result so the merged stats, gaps and the gap analysis follow
            onCoverageUpdated(coverage) {
                if (!this.detail || !coverage) return
                this.$set(this.detail, "coverage", coverage)
                const stored = this.results.find((r) => String(r._id) === String(this.detail._id))
                if (stored && stored !== this.detail) this.$set(stored, "coverage", coverage)
                this.notification = this.$helpers.createNotification("Coverage re-checked for this page.", "mdi-check-circle", "primary")
                // the checked prompts changed, so the measured rows have to follow
                this.loadTracking()
            },
            // measured visibility of the opened page; a stale response (page changed, run
            // changed) is dropped by the token instead of overwriting the newer state
            async loadTracking() {
                const token = ++this.trackingToken
                if (!this.hasProject || !this.detail || this.detail.status !== "done") {
                    this.tracking = null
                    this.trackingLoading = false
                    this.trackingError = ""
                    return
                }
                const resultId = String(this.detail._id)
                try {
                    this.trackingLoading = true
                    this.trackingError = ""
                    const data = await fetchCoverageTracking(localStorage.company_id, { runId: this.$route.params.id, resultId })
                    if (this.isDestroyed || token !== this.trackingToken) return
                    this.tracking = data && data.tracking && typeof data.tracking === "object" ? data.tracking : null
                } catch (error) {
                    if (this.isDestroyed || token !== this.trackingToken) return
                    console.error("loadTracking error", error)
                    this.tracking = null
                    this.trackingError = "Measured visibility could not be loaded."
                } finally {
                    if (!this.isDestroyed && token === this.trackingToken) this.trackingLoading = false
                }
            },
            // roving tabindex (WAI-ARIA tabs pattern): arrow keys move between the tabs, Home/End
            // jump to the first/last one; the newly selected tab receives focus
            onTabKeydown(event, index) {
                const tabs = this.visibleTabs
                if (!tabs.length) return
                let next = null
                if (event.key === "ArrowRight") next = (index + 1) % tabs.length
                else if (event.key === "ArrowLeft") next = (index - 1 + tabs.length) % tabs.length
                else if (event.key === "Home") next = 0
                else if (event.key === "End") next = tabs.length - 1
                if (next === null) return
                event.preventDefault()
                this.activeTab = tabs[next].key
                this.$nextTick(() => {
                    const el = this.$el && this.$el.querySelector ? this.$el.querySelector("#audit-tab-" + tabs[next].key) : null
                    if (el && el.focus) el.focus()
                })
            },
            async openResult(row) {
                if (!row || !row._id) return
                const local = this.results.find((r) => String(r._id) === String(row._id))
                if (local) {
                    this.detail = local
                    return
                }
                try {
                    this.detailLoading = true
                    const results = await fetchResults(localStorage.company_id, this.$route.params.id, row._id)
                    this.detail = results.length ? results[0] : null
                    if (!this.detail) {
                        this.notification = this.$helpers.createNotification("Result not found.", "mdi-alert-circle-outline", "error")
                    }
                } catch (error) {
                    console.error("openResult error", error)
                    const message = error?.response?.data?.error || "Error loading the result."
                    this.notification = this.$helpers.createNotification(message, "mdi-alert-circle-outline", "error")
                } finally {
                    this.detailLoading = false
                }
            },
            closeDetail() {
                this.detail = null
            },
            // start a brand new run for the same URLs and open it (the button label is "Re-check")
            async reRun() {
                if (!this.run || this.reRunning || !this.$store.getters.canWriteAiSuite) return
                const urls = Array.isArray(this.run.urls) && this.run.urls.length ? this.run.urls : this.summary.map((r) => r.url).filter(Boolean)
                if (!urls.length) {
                    this.notification = this.$helpers.createNotification("No URLs available to re-check.", "mdi-alert-circle-outline", "error")
                    return
                }
                try {
                    this.reRunning = true
                    const data = await createRun(localStorage.company_id, {
                        urls,
                        name: this.run.name || null,
                        projectId: this.run.project_id || null,
                        // the focus entries are part of what was audited: without them the re-check
                        // would silently be a different audit under the same name
                        focus: this.runFocusEntries
                    })
                    if (data && data.error) {
                        if (data.error === "not_enough_credits" || data.error === "not enough credits") {
                            const available = Math.max(0, (Number(data.credits) || 0) - (Number(data.count) || 0))
                            const needed = Number(data.credits_needed) || 0
                            this.notification = this.$helpers.createNotification("Not enough audit credits: " + available + " available, " + needed + " needed.", "mdi-alert-circle-outline", "error")
                        } else {
                            this.notification = this.$helpers.createNotification(data.error, "mdi-alert-circle-outline", "error")
                        }
                        return
                    }
                    if (data && data.status === "started" && data.run_id) {
                        this.notification = this.$helpers.createNotification("Re-check started!", "mdi-check-circle", "primary")
                        if (String(data.run_id) === String(this.$route.params.id)) {
                            this.loadRun()
                        } else {
                            this.$router.push("/ai-suite/audit/run/" + data.run_id)
                        }
                        return
                    }
                    this.notification = this.$helpers.createNotification("Unexpected response. Please try again later.", "mdi-alert-circle-outline", "error")
                } catch (error) {
                    console.error("reRun error", error)
                    const message = error?.response?.data?.error || "Error starting the re-check. Please try again later."
                    this.notification = this.$helpers.createNotification(message, "mdi-alert-circle-outline", "error")
                } finally {
                    this.reRunning = false
                }
            }
        },
        watch: {
            // a tab whose panel disappeared (detail closed, coverage gone) must not stay selected
            visibleTabs(tabs) {
                if (!tabs.some((tab) => tab.key === this.activeTab)) this.activeTab = tabs.length ? tabs[0].key : "overview"
            },
            // the measured rows belong to ONE page: reload them whenever another page is opened
            detail(value, previous) {
                const id = value && value._id ? String(value._id) : ""
                const prev = previous && previous._id ? String(previous._id) : ""
                if (id === prev) return
                this.tracking = null
                this.trackingError = ""
                this.loadTracking()
            },
            // navigating from one run to another reuses this component; reset state and reload
            "$route.params.id"() {
                this.stopPolling()
                this.loading = true
                this.run = null
                this.summary = []
                this.results = []
                this.resultsLoading = false
                this.resultsLoaded = false
                this.expandedActions = {}
                this.expandedRecoMaterial = {}
                this.detail = null
                this.activeCategory = null
                this.activeTab = "overview"
                this.passedOpen = false
                this.coverageGapsOpen = false
                // an in-flight generation belongs to the previous run: invalidate it instead of
                // dropping the flag underneath it, so it cannot race the next generation
                this.fixesRequestToken++
                this.fixesLoadingId = null
                this.trackingToken++
                this.tracking = null
                this.trackingLoading = false
                this.trackingError = ""
                this.loadRun()
            }
        },
        mounted() {
            this.loadRun()
        },
        beforeDestroy() {
            this.isDestroyed = true
            this.stopPolling()
        }
    }
</script>

<style lang="scss" scoped>
    @import url("https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap");

    .quaro-audit {
        font-family: "Plus Jakarta Sans", system-ui, -apple-system, sans-serif;
        color: #0d2622;
        background: #f2faf8;
    }
    .quaro-audit ::v-deep .v-btn {
        text-transform: none;
        letter-spacing: normal;
        font-family: inherit;
        font-weight: 600;
        box-shadow: none !important;
    }
    .quaro-audit ::v-deep .v-card {
        font-family: inherit;
    }
    /* visible keyboard focus on everything operable (BITV 2.0 / WCAG 2.4.7) */
    .quaro-audit ::v-deep button:focus-visible,
    .quaro-audit ::v-deep a:focus-visible,
    .quaro-audit ::v-deep [role="button"]:focus-visible,
    .quaro-audit ::v-deep [role="tab"]:focus-visible,
    .quaro-audit ::v-deep [tabindex="0"]:focus-visible {
        outline: 2px solid #0d2622;
        outline-offset: 2px;
    }
    /* native buttons used as inline controls: no browser chrome, text styles come from the class */
    .seoaudit-run__infobtn,
    .seoaudit-run__urlbtn,
    .seoaudit-run__expandtoggle,
    .seoaudit-run__tab {
        background: transparent;
        border: 0;
        padding: 0;
        margin: 0;
        font-family: inherit;
        text-align: left;
    }
    .seoaudit-run__infobtn {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        font-size: 12px;
        font-weight: 600;
        color: #3d6d66;
        cursor: pointer;
        padding: 2px 6px;
        border-radius: 6px;
    }
    .seoaudit-run__infobtn:hover {
        background: #eaf5f3;
    }
    .seoaudit-run__help {
        margin-top: 8px;
        padding: 10px 12px;
        border-radius: 8px;
        background: #ffffff;
        border: 1px solid #d4ede9;
        display: flex;
        flex-direction: column;
        gap: 6px;
    }
    .seoaudit-run__geointro {
        font-size: 13px;
        color: #3d6d66;
    }
    .seoaudit-run__gaplist {
        list-style: none;
        padding: 0;
        margin: 0;
    }
    .seoaudit-run__urlbtn {
        cursor: pointer;
        color: inherit;
        font-size: inherit;
    }
    .quaro-shadow {
        box-shadow: 0 1px 4px rgba(0, 159, 137, 0.08), 0 4px 16px rgba(0, 0, 0, 0.06) !important;
        border: 1px solid #d4ede9;
    }
    .q-eyebrow {
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: #3d6d66;
    }
    .q-muted {
        color: #3d6d66;
    }
    .q-primary-text {
        color: #007a68;
    }
    .q-accent-text {
        color: #007a68;
    }
    .q-pill {
        display: inline-flex;
        align-items: center;
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 0.02em;
        border-radius: 999px;
        padding: 3px 10px;
        color: #ffffff;
        background: #3d6d66;
    }
    .q-pill--block {
        border-radius: 6px;
        justify-content: center;
    }
    .q-pill--outline {
        background: transparent;
        border: 1px solid #d4ede9;
        color: #3d6d66;
    }
    .q-pill--primary {
        background: #007a68;
        color: #ffffff;
    }
    .q-pill--outline.q-pill--primary {
        background: #e0f5f2;
        border-color: #d4ede9;
        color: #007a68;
    }
    .q-pill--danger {
        background: #b32d2d;
    }
    .q-pill--warn {
        background: #9a6700;
    }
    .q-pill--info {
        background: #0077cc;
    }
    .q-pill--grey {
        background: #3d6d66;
    }
    .q-tag {
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 0.02em;
        text-transform: uppercase;
        color: #3d6d66;
        border: 1px solid #d4ede9;
        border-radius: 6px;
        padding: 2px 8px;
        margin-left: 8px;
    }
    .q-btn--ghost {
        color: #3d6d66 !important;
    }
    .q-dot--primary {
        background: #007a68;
    }
    .q-dot--warn {
        background: #9a6700;
    }
    .q-dot--danger {
        background: #b32d2d;
    }
    .q-dot--grey {
        background: #3d6d66;
    }

    .seoaudit-run__hero {
        overflow: hidden;
    }
    .seoaudit-run__herobar {
        display: flex;
        align-items: center;
        padding: 20px 24px 0;
    }
    .seoaudit-run__titles {
        display: flex;
        flex-direction: column;
        gap: 2px;
    }
    .seoaudit-run__title {
        margin-bottom: 0;
    }
    .seoaudit-run__subtitle {
        color: #3d6d66;
        font-size: 13px;
        display: flex;
        align-items: center;
        gap: 8px;
    }
    .seoaudit-run__progress {
        padding: 0 24px 24px;
        margin-top: 20px;
    }
    .seoaudit-run__progresstext {
        font-size: 12px;
        color: white;
    }
    .seoaudit-run__summary {
        display: grid;
        grid-template-columns: 260px 1fr;
    }
    .seoaudit-run__ring {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 12px;
        padding: 28px 24px;
        background: linear-gradient(160deg, #0a1f1c 0%, #0d3330 100%);
    }
    .seoaudit-run__ringcaption {
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: #007a68;
    }
    .seoaudit-run__ringsummary {
        font-size: 12px;
        color: rgba(255, 255, 255, 0.6);
        text-align: center;
        max-width: 190px;
    }
    .seoaudit-run__tiles {
        padding: 24px 28px;
        display: flex;
        flex-direction: column;
        gap: 16px;
    }
    .seoaudit-run__tileshead {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: 16px;
    }
    .seoaudit-run__clearfilter {
        font-size: 13px;
        color: #007a68;
        cursor: pointer;
        font-weight: 500;
    }
    .seoaudit-run__tilegrid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 12px 24px;
    }
    .seoaudit-run__tile {
        display: flex;
        flex-direction: column;
        gap: 7px;
        padding: 12px 14px;
        border-radius: 12px;
        border: 1px solid #d4ede9;
        background: #ffffff;
        cursor: pointer;
        transition: border-color 150ms ease;
    }
    .seoaudit-run__tile:hover {
        border-color: #007a68;
    }
    .seoaudit-run__tile--active {
        border-color: #007a68;
        background: #e0f5f2;
    }
    .seoaudit-run__tilerow {
        display: flex;
        align-items: baseline;
        gap: 8px;
    }
    .seoaudit-run__tilelabel {
        font-size: 14px;
        font-weight: 600;
    }
    .seoaudit-run__tilescore {
        margin-left: auto;
        font-size: 22px;
        font-weight: 700;
        letter-spacing: -0.02em;
    }
    .seoaudit-run__tilebar {
        height: 5px;
        border-radius: 999px;
        background: #eaf5f3;
        overflow: hidden;
    }
    .seoaudit-run__tilebarfill {
        height: 100%;
        border-radius: 999px;
        background: linear-gradient(90deg, #007a68, #1cedb3);
    }
    .seoaudit-run__tilenote {
        font-size: 12px;
        font-weight: 500;
        color: #3d6d66;
    }
    .seoaudit-run__tilenote--issue {
        color: #b32d2d;
    }
    .seoaudit-run__tilenote--warn {
        color: #9a6700;
    }
    .seoaudit-run__tilenote--ok {
        color: #007a68;
    }
    .seoaudit-run__potential {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 14px 18px;
        border-radius: 12px;
        background: #e8fdf7;
        border: 1px solid #d4ede9;
        font-size: 14px;
    }

    /* STICKY HEADER: title, status, score and tabs in one bar */
    .seoaudit-run__topbar {
        position: sticky;
        top: 0;
        z-index: 6;
        margin: 0 -12px;
        padding: 10px 12px 0;
        background: rgba(242, 250, 248, 0.94);
        backdrop-filter: blur(10px);
        border-bottom: 1px solid #d4ede9;
    }
    .seoaudit-run__topbarrow {
        display: flex;
        align-items: center;
        gap: 12px;
        flex-wrap: wrap;
    }
    .seoaudit-run__topscore {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-left: 16px;
        padding-left: 20px;
        border-left: 1px solid #d4ede9;
    }
    .seoaudit-run__topscorevalue {
        font-size: 30px;
        font-weight: 700;
        letter-spacing: -0.03em;
        line-height: 1;
    }
    .seoaudit-run__topscorelabel {
        display: flex;
        flex-direction: column;
        gap: 1px;
    }
    .seoaudit-run__topscorehint {
        font-size: 12px;
        color: #2d5a54;
    }

    /* OVERVIEW: two columns — diagnosis left, action right (the material card needs the wider side) */
    .seoaudit-run__ovgrid {
        display: grid;
        grid-template-columns: minmax(0, 5fr) minmax(0, 7fr);
        gap: 20px;
        align-items: start;
    }
    .seoaudit-run__ovcol {
        display: flex;
        flex-direction: column;
        gap: 20px;
        min-width: 0;
    }
    .seoaudit-run__catlist {
        display: flex;
        flex-direction: column;
        gap: 14px;
        margin-top: 14px;
    }
    .seoaudit-run__cat {
        display: flex;
        flex-direction: column;
        gap: 6px;
        cursor: pointer;
    }
    .seoaudit-run__catrow {
        display: flex;
        align-items: baseline;
        gap: 10px;
    }
    .seoaudit-run__catscore {
        margin-left: auto;
        font-size: 18px;
        font-weight: 700;
        letter-spacing: -0.02em;
    }
    .seoaudit-run__toplist {
        display: flex;
        flex-direction: column;
        gap: 8px;
        margin-top: 12px;
    }
    .seoaudit-run__progressdate {
        font-size: 12px;
        color: #3d6d66;
    }
    .seoaudit-run__progressrow {
        display: flex;
        align-items: baseline;
        gap: 12px;
        margin-top: 12px;
    }
    .seoaudit-run__progressdelta {
        font-size: 26px;
        font-weight: 700;
        letter-spacing: -0.02em;
        color: #3d6d66;
    }
    .seoaudit-run__progressdelta--up {
        color: #007a68;
    }
    .seoaudit-run__progressdelta--down {
        color: #b32d2d;
    }
    .seoaudit-run__progresstextline {
        font-size: 14px;
    }
    .seoaudit-run__progresslist {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 6px;
        margin-top: 12px;
    }
    .seoaudit-run__progresshead {
        font-size: 12px;
        font-weight: 600;
        color: #3d6d66;
        min-width: 78px;
    }
    .seoaudit-run__progresspill--issue {
        background: #fdecec;
        border-color: #f6cfcf;
        color: #b32d2d;
    }
    .seoaudit-run__progressnote {
        font-size: 12px;
        color: #3d6d66;
        margin: 12px 0 0;
    }
    .seoaudit-run__toprow {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 10px 12px;
        border: 1px solid #eaf5f3;
        border-radius: 10px;
        cursor: pointer;
        transition: all 150ms ease;
    }
    .seoaudit-run__toprow:hover {
        border-color: #007a68;
        background: #f2faf8;
    }
    .seoaudit-run__toptitle {
        font-size: 14px;
        font-weight: 500;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    .seoaudit-run__ovlink {
        font-size: 13px;
        font-weight: 600;
        color: #007a68;
        cursor: pointer;
        white-space: nowrap;
    }
    .seoaudit-run__ovlink--accent {
        /* sits on the dark gradient card: light accent keeps 10:1 */
        color: #1cedb3;
    }
    .seoaudit-run__ovcoverage {
        background: linear-gradient(160deg, #0a1f1c 0%, #0d3330 100%);
        border-radius: 16px;
        padding: 20px 24px;
        display: flex;
        flex-direction: column;
        gap: 18px;
    }
    .seoaudit-run__ovstats {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 18px;
    }
    .seoaudit-run__ovstat {
        display: flex;
        flex-direction: column;
        gap: 4px;
    }
    .seoaudit-run__ovstatvalue {
        font-size: 26px;
        font-weight: 700;
        letter-spacing: -0.03em;
        line-height: 1;
        color: #ffffff;
    }
    /* on the dark gradient card the LIGHT shades are the accessible ones (10:1 and 7.8:1) */
    .seoaudit-run__ovstatvalue--accent {
        color: #1cedb3;
    }
    .seoaudit-run__ovstatvalue--warn {
        color: #e5a400;
    }
    .seoaudit-run__ovstatlabel {
        font-size: 12px;
        color: rgba(255, 255, 255, 0.6);
    }
    .seoaudit-run__ovcoveragefoot {
        display: flex;
        align-items: center;
        gap: 12px;
        font-size: 13px;
        color: rgba(255, 255, 255, 0.75);
    }
    .seoaudit-run__ovcard {
        cursor: pointer;
    }
    .seoaudit-run__ovcard:hover {
        border-color: #007a68;
    }
    .seoaudit-run__ovcardvalue {
        display: block;
        font-size: 15px;
        font-weight: 600;
        margin-top: 6px;
    }
    .seoaudit-run__ovcardhint {
        display: block;
        font-size: 13px;
        color: #3d6d66;
        margin-top: 4px;
    }
    .seoaudit-run__ovmaterialintro {
        margin: 10px 0 12px;
        font-size: 13px;
    }
    .seoaudit-run__ovmatrow {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 9px 0;
        border-bottom: 1px solid #f1f6f5;
    }
    .seoaudit-run__ovmatrow:last-of-type {
        border-bottom: 0;
    }
    .seoaudit-run__ovmatbody {
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 0;
        flex: 1 1 auto;
    }
    .seoaudit-run__ovmatlabel {
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: #3d6d66;
    }
    .seoaudit-run__ovmatpreview {
        font-size: 13px;
        color: #0d2622;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    .seoaudit-run__ovmatcopy {
        flex: 0 0 auto;
    }

    .seoaudit-run__columns {
        display: block;
        margin-top: 20px;
    }
    /* every tab panel is one full-width stack; only one is shown at a time */
    .seoaudit-run__panel {
        display: flex;
        flex-direction: column;
        gap: 20px;
        min-width: 0;
    }
    /* the tab row lives in the sticky header now */
    .seoaudit-run__tabs {
        display: flex;
        align-items: center;
        gap: 4px;
        flex-wrap: wrap;
    }
    .seoaudit-run__tab {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        height: 44px;
        padding: 0 14px;
        border-radius: 6px 6px 0 0;
        font-size: 14px;
        font-weight: 500;
        color: #2d5a54;
        border-bottom: 2px solid transparent;
        cursor: pointer;
        user-select: none;
        white-space: nowrap;
    }
    .seoaudit-run__tab:hover {
        color: #007a68;
    }
    .seoaudit-run__tab--active {
        color: #007a68;
        font-weight: 600;
        border-bottom-color: #007a68;
    }
    .seoaudit-run__tab:focus-visible {
        outline-offset: -2px;
    }
    .seoaudit-run__tabcount {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 20px;
        height: 20px;
        padding: 0 6px;
        border-radius: 999px;
        background: #eaf5f3;
        color: #3d6d66;
        font-size: 11px;
        font-weight: 700;
    }
    .seoaudit-run__tab--active .seoaudit-run__tabcount {
        background: #e0f5f2;
        color: #007a68;
    }
    /* 15 passage cards stacked in a 320px column was the longest block on the page */
    .seoaudit-run__passagescard ::v-deep .v-card__text {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 16px;
        align-items: start;
    }
    .seoaudit-run__passagescard ::v-deep .seoaudit-run__sidehead,
    .seoaudit-run__passagescard ::v-deep .seoaudit-run__passageok {
        grid-column: 1 / -1;
    }
    .seoaudit-run__main {
        display: flex;
        flex-direction: column;
        gap: 20px;
        min-width: 0;
    }
    .seoaudit-run__side {
        display: flex;
        flex-direction: column;
        gap: 20px;
        min-width: 0;
    }
    /* the generated material (HTML tables, JSON-LD) needs the full width of the main column, not the
       320px side column — it lives right below the recommendations it belongs to */
    .seoaudit-run__fixescard {
        min-width: 0;
    }
    .seoaudit-run__urltable ::v-deep tbody tr {
        cursor: pointer;
    }
    .seoaudit-run__tablehint {
        padding: 0 16px 14px;
        font-size: 12px;
        color: #3d6d66;
    }
    .seoaudit-run__cellscore {
        font-size: 13px;
        font-weight: 700;
    }
    .seoaudit-run__sidecard {
        background: #ffffff;
    }
    .seoaudit-run__sidecard ::v-deep .v-card__text {
        color: #0d2622;
    }
    .seoaudit-run__sidecard .seoaudit-run__sidehead {
        display: flex;
        align-items: center;
    }
    .seoaudit-run__pageurl {
        font-size: 13px;
        line-height: 1.5;
        word-break: break-all;
        margin-top: 8px;
        color: #0d2622;
    }
    .seoaudit-run__divider {
        height: 1px;
        background: #eaf5f3;
        margin: 16px 0;
    }
    .seoaudit-run__signal {
        display: flex;
        align-items: center;
        gap: 10px;
        font-size: 13px;
        color: #2d5a54;
        padding: 4px 0;
    }
    .seoaudit-run__dot {
        width: 7px;
        height: 7px;
        border-radius: 999px;
        flex: 0 0 auto;
    }
    .seoaudit-run__quote {
        border-left: 3px solid #007a68;
        padding-left: 12px;
        margin-top: 14px;
    }
    .seoaudit-run__quotetext {
        font-size: 13px;
        line-height: 1.55;
        font-style: italic;
        color: #0d2622;
    }
    .seoaudit-run__quotereason {
        font-size: 12px;
        color: #3f6b63;
        margin-top: 5px;
    }

    .seoaudit-run__actionshead {
        display: flex;
        align-items: center;
    }
    .seoaudit-run__emptystate {
        display: flex;
        align-items: center;
        padding: 14px 0;
        font-size: 15px;
        font-weight: 500;
        color: #007a68;
    }
    .seoaudit-run__action {
        display: flex;
        align-items: flex-start;
        gap: 14px;
        padding: 16px 0;
        border-bottom: 1px solid #eaf5f3;
        &:last-of-type {
            border-bottom: none;
        }
    }
    .seoaudit-run__actionprio {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 6px;
        flex: 0 0 auto;
        width: 60px;
    }
    .seoaudit-run__actionimpact {
        font-size: 12px;
        font-weight: 700;
        color: #007a68;
    }
    .seoaudit-run__actionbody {
        display: flex;
        flex-direction: column;
        gap: 6px;
        min-width: 0;
    }
    .seoaudit-run__actiontitle {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
    }
    .seoaudit-run__actionreco {
        font-size: 14px;
        line-height: 1.5;
        color: #2d5a54;
    }
    .seoaudit-run__expandtoggle {
        display: inline-flex;
        align-items: center;
        font-size: 13px;
        font-weight: 500;
        color: #007a68;
        cursor: pointer;
        margin-top: 4px;
        user-select: none;
        width: fit-content;
    }
    .seoaudit-run__actiondetailbox {
        margin-top: 4px;
        display: flex;
        flex-direction: column;
        gap: 10px;
        padding: 14px 16px;
        border-radius: 10px;
        background: #f2faf8;
        border: 1px solid #eaf5f3;
    }
    .seoaudit-run__detailrow {
        display: flex;
        gap: 10px;
    }
    .seoaudit-run__detaillabel {
        flex: 0 0 96px;
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: #3d6d66;
    }
    .seoaudit-run__detailvalue {
        font-size: 13px;
        font-family: monospace;
    }
    .seoaudit-run__detailtext {
        font-size: 13px;
        line-height: 1.55;
        color: #2d5a54;
    }
    .seoaudit-run__actionurls {
        margin-top: 6px;
        padding-left: 8px;
        border-left: 2px solid #eaf5f3;
    }
    .seoaudit-run__actionurl {
        display: flex;
        flex-direction: column;
        padding: 4px 0;
    }
    .seoaudit-run__actionurldetails {
        font-size: 12px;
        color: #3d6d66;
    }
    .seoaudit-run__url {
        word-break: break-all;
        font-size: 13px;
    }
    .seoaudit-run__passedwrap {
        border-top: 1px solid #eaf5f3;
        margin-top: 10px;
        padding-top: 14px;
    }
    .seoaudit-run__passedtoggle {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        font-size: 14px;
        font-weight: 500;
        color: #3d6d66;
        cursor: pointer;
        user-select: none;
        &:hover {
            color: #007a68;
        }
    }
    .seoaudit-run__passedgrid {
        margin-top: 12px;
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 8px 24px;
    }
    .seoaudit-run__passeditem {
        display: flex;
        align-items: baseline;
        gap: 8px;
        padding: 5px 0;
        font-size: 13px;
        color: #2d5a54;
    }
    .seoaudit-run__passedsubhead {
        display: block;
        margin-top: 16px;
    }
    .seoaudit-run__passeditem--muted {
        color: #3d6d66;
    }
    .seoaudit-run__passedvalue {
        margin-left: auto;
        font-size: 12px;
        color: #3d6d66;
        font-family: monospace;
    }
    .seoaudit-run__recohead {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 10px;
        margin-top: 24px;
        margin-bottom: 8px;
    }
    .seoaudit-run__reco {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        padding: 4px 0;
        font-size: 14px;
    }
    .seoaudit-run__recocopy {
        flex: 0 0 auto;
        margin-top: 1px;
    }

    /* small standalone chips: facts density + passage problems + fan-out usage */
    .seoaudit-run__chip {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 0.02em;
        border-radius: 999px;
        padding: 3px 10px;
        border: 1px solid #d4ede9;
        background: #ffffff;
        color: #3d6d66;
    }
    .seoaudit-run__chip--success {
        border-color: #b9e5dd;
        background: #e0f5f2;
        color: #007a68;
    }
    .seoaudit-run__chip--warn {
        border-color: #f3e2b6;
        background: #fdf6e4;
        color: #8a5200;
    }
    .seoaudit-run__chip--danger {
        border-color: #f2cccc;
        background: #fceaea;
        color: #b32d2d;
    }
    .seoaudit-run__detailchips {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        margin-top: 10px;
    }

    /* GEO COVERAGE */
    .seoaudit-run__geostat {
        display: flex;
        flex-direction: column;
        gap: 2px;
        padding: 14px 16px;
        border-radius: 12px;
        border: 1px solid #d4ede9;
        background: #ffffff;
        height: 100%;
    }
    .seoaudit-run__geoscore {
        font-size: 30px;
        font-weight: 700;
        line-height: 1.2;
        letter-spacing: -0.02em;
    }
    .seoaudit-run__geocaption {
        font-size: 13px;
        color: #2d5a54;
        line-height: 1.45;
    }
    /* half emptied by the per-URL relevance filter — grey statement instead of a 0% score */
    .seoaudit-run__geonotrelevant {
        font-size: 17px;
        font-weight: 600;
        line-height: 1.2;
        color: #3d6d66;
        padding: 6px 0 5px;
    }
    .seoaudit-run__geohint {
        font-size: 12px;
        color: #3d6d66;
        margin-top: 4px;
        line-height: 1.45;
    }
    /* content fit vs measured visibility — shown under the intro, before the percentages */
    .seoaudit-run__geoexplain {
        font-size: 12px;
        color: #2d5a54;
        line-height: 1.5;
        margin-top: 8px;
        padding: 8px 12px;
        border-radius: 8px;
        background: #f3faf8;
        border: 1px solid #d4ede9;
    }
    .seoaudit-run__tipblock {
        font-size: 12px;
        line-height: 1.45;
    }
    .seoaudit-run__tipblock + .seoaudit-run__tipblock {
        margin-top: 6px;
    }
    .seoaudit-run__gapshead {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 10px;
        margin-top: 14px;
        padding-top: 14px;
        border-top: 1px solid #eaf5f3;
    }
    .seoaudit-run__gap {
        display: flex;
        align-items: flex-start;
        gap: 14px;
        padding: 12px 0;
        border-bottom: 1px solid #eaf5f3;
        &:last-of-type {
            border-bottom: none;
        }
    }
    .seoaudit-run__gaptags {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 6px;
        flex: 0 0 auto;
        width: 72px;
    }
    .seoaudit-run__gapbody {
        display: flex;
        flex-direction: column;
        gap: 4px;
        min-width: 0;
        font-size: 14px;
        line-height: 1.5;
    }
    .seoaudit-run__gaptext {
        font-size: 13px;
        color: #2d5a54;
    }

    /* PASSAGE QUALITY */
    .seoaudit-run__passagecount {
        color: #b32d2d;
    }
    .seoaudit-run__passage {
        display: flex;
        flex-direction: column;
        gap: 6px;
        padding: 14px 0;
        border-bottom: 1px solid #eaf5f3;
        &:last-of-type {
            border-bottom: none;
        }
    }
    .seoaudit-run__passagehead {
        display: flex;
        align-items: baseline;
        gap: 8px;
        font-size: 13px;
        line-height: 1.45;
    }
    .seoaudit-run__passagewords {
        margin-left: auto;
        flex: 0 0 auto;
        font-size: 12px;
        color: #3d6d66;
        font-family: monospace;
    }
    .seoaudit-run__passagetags {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
    }
    .seoaudit-run__passageissue {
        font-size: 13px;
        line-height: 1.5;
        color: #2d5a54;
    }
    .seoaudit-run__suggestion {
        display: flex;
        flex-direction: column;
        gap: 4px;
        padding: 10px 12px;
        border-radius: 10px;
        background: #f2faf8;
        border: 1px solid #eaf5f3;
    }
    .seoaudit-run__suggestionhead {
        display: flex;
        align-items: center;
    }
    .seoaudit-run__suggestiontext {
        font-size: 13px;
        line-height: 1.55;
        font-style: italic;
        color: #0d2622;
    }
    .seoaudit-run__passageok {
        display: flex;
        align-items: center;
        margin-top: 12px;
        padding-top: 12px;
        border-top: 1px solid #eaf5f3;
        font-size: 13px;
        color: #007a68;
    }
    /* READY-TO-USE FIXES */
    .seoaudit-run__fixesintro {
        font-size: 13px;
        line-height: 1.5;
        margin: 10px 0 14px;
    }
    .seoaudit-run__fixblock {
        display: flex;
        flex-direction: column;
        gap: 6px;
        padding: 14px 0;
        border-bottom: 1px solid #eaf5f3;
        &:last-of-type {
            border-bottom: none;
        }
    }
    .seoaudit-run__fixhead {
        display: flex;
        align-items: center;
        gap: 8px;
        min-height: 22px;
    }
    .seoaudit-run__charcount {
        font-size: 11px;
        font-family: monospace;
        color: #3d6d66;
    }
    .seoaudit-run__charcount--warn {
        color: #8a5200;
    }
    .seoaudit-run__fixbox {
        padding: 10px 12px;
        border-radius: 10px;
        background: #f2faf8;
        border: 1px solid #eaf5f3;
        font-size: 13px;
        line-height: 1.55;
        color: #0d2622;
        white-space: pre-wrap;
        word-break: break-word;
    }
    /* JSON-LD keeps its own line breaks: never wrap, scroll horizontally instead */
    .seoaudit-run__pre {
        margin: 0;
        padding: 10px 12px;
        border-radius: 10px;
        background: #f2faf8;
        border: 1px solid #eaf5f3;
        color: #0d2622;
        font-family: monospace;
        font-size: 12px;
        line-height: 1.5;
        white-space: pre;
        overflow-x: auto;
        overflow-y: auto;
        /* multi-kilobyte tables and JSON-LD payloads: tall enough to read, still capped */
        max-height: 560px;
    }
    /* HTML snippets have no meaningful indentation and are often a single long line */
    .seoaudit-run__pre--wrap {
        white-space: pre-wrap;
        word-break: break-word;
        overflow-x: hidden;
    }
    /* generated placeholders like "[ADD: current price]" — must be impossible to overlook */
    .seoaudit-run__placeholder {
        background: #fdf6e4;
        border-bottom: 1px dashed #9a6700;
        border-radius: 3px;
        padding: 0 2px;
        color: #8a5200;
        font-weight: 600;
    }
    /* ready-to-paste material blocks derived from the AI content recommendations */
    .seoaudit-run__blocktitle {
        font-size: 13px;
        line-height: 1.45;
        min-width: 0;
        word-break: break-word;
    }
    .seoaudit-run__blockkind {
        flex: 0 0 auto;
    }
    .seoaudit-run__blocknote {
        font-size: 12px;
        line-height: 1.45;
        color: #3d6d66;
    }
    .seoaudit-run__blockreco {
        font-size: 12px;
        line-height: 1.45;
        color: #3d6d66;
    }
    .seoaudit-run__faqitem {
        display: flex;
        flex-direction: column;
        gap: 4px;
        padding: 8px 0;
        border-bottom: 1px solid #eaf5f3;
        &:first-of-type {
            padding-top: 0;
        }
        &:last-of-type {
            padding-bottom: 0;
            border-bottom: none;
        }
    }
    .seoaudit-run__faqhead {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 8px;
        font-size: 13px;
        line-height: 1.45;
    }
    .seoaudit-run__faqanswer {
        font-size: 13px;
        line-height: 1.55;
        color: #2d5a54;
    }
    .seoaudit-run__faqhint {
        font-size: 12px;
        color: #8a5200;
    }
    .seoaudit-run__factlist {
        margin: 0;
        padding-left: 18px;
        font-size: 13px;
        line-height: 1.55;
        color: #2d5a54;
        li {
            padding: 3px 0;
        }
    }
    .seoaudit-run__fixesactions {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 8px;
        margin-top: 14px;
    }
    /* subtle: the card is usable without it, it only explains what Regenerate would add */
    .seoaudit-run__regeneratehint {
        font-size: 12px;
        line-height: 1.45;
        color: #3d6d66;
    }
    .seoaudit-run__fixescaption {
        display: block;
        margin-top: 8px;
        font-size: 12px;
        color: #3d6d66;
    }
    /* same caption, but above the recommendation list instead of at the end of the card */
    .seoaudit-run__fixescaption--head {
        margin: 0 0 8px;
    }
    /* names the URL the generated material belongs to — the list itself is run-wide */
    .seoaudit-run__recoscope {
        font-size: 12px;
        color: #3d6d66;
        word-break: break-all;
    }

    .seoaudit-run__recobody {
        display: flex;
        flex-direction: column;
        /* the inline material lives in here: without this a wide JSON-LD block would stretch the row
           instead of scrolling inside its own box */
        flex: 1 1 auto;
        min-width: 0;
    }
    .seoaudit-run__recourl {
        font-size: 12px;
        color: #3d6d66;
    }
    /* captions of the recommendation list: what the generate button does / what it is doing */
    .seoaudit-run__recocaption {
        font-size: 13px;
        line-height: 1.5;
        margin: 2px 0 8px;
    }
    /* per-row states without material: nothing to do here, or the wrong URL is open */
    .seoaudit-run__recohint {
        font-size: 12px;
        line-height: 1.45;
        color: #3d6d66;
        margin-top: 2px;
    }
    .seoaudit-run__recotoggle {
        gap: 6px;
        margin-bottom: 2px;
    }
    .seoaudit-run__recocaret {
        font-size: 11px;
        line-height: 1;
    }
    /* the generated blocks of one recommendation, indented under the line they realize */
    .seoaudit-run__recomaterial {
        margin: 2px 0 6px;
        padding-left: 12px;
        border-left: 2px solid #d4ede9;
        min-width: 0;
    }
    .seoaudit-run__recomaterial .seoaudit-run__fixblock {
        padding: 8px 0;
    }

    @media (max-width: 960px) {
        .seoaudit-run__ovgrid,
        .seoaudit-run__ovstats {
            grid-template-columns: 1fr;
        }
        .seoaudit-run__passagescard ::v-deep .v-card__text {
            grid-template-columns: 1fr;
        }
        .seoaudit-run__summary,
        .seoaudit-run__columns {
            grid-template-columns: 1fr;
        }
        .seoaudit-run__tilegrid {
            grid-template-columns: 1fr;
        }
    }
</style>
